import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    IconButton,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    InputAdornment,
    useMediaQuery,
    useTheme,
    RadioGroup,
    Radio,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Checkbox,
    Paper,
    Stack,
    Divider,
    Chip,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SearchIcon from '@mui/icons-material/Search';
import NotesIcon from '@mui/icons-material/Notes';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PaymentIcon from '@mui/icons-material/Payment';
import { Edit, DeleteOutline } from '@mui/icons-material';
import ImputableSearch from '../ImputableSearch';
import ImputableInlineSearchField from './ImputableInlineSearchField';
import { Imputable } from '../../services/imputableService';
import { formatCurrency, truncToDecimals } from '../../utils/formatters';
import tesoreriaService, { GrupoCajaChica, MedioTransferencia, Banco, MarcaTarjeta, ChequeCartera, CuponCartera } from '../../services/tesoreriaService';
import SearchableSelect from '../SearchableSelect';
import GrupoCajaSelect from './GrupoCajaSelect';
import { findGrupoCajaGuardado, persistGrupoCajaChica } from './grupoCajaStorage';

const TIPO_PAGO_OPTIONS = [
    { value: 'Efectivo', label: 'Efectivo' },
    { value: 'Transferencia', label: 'Transferencias' },
    { value: 'Cheque', label: 'Cheques de Tercero' },
    { value: 'Tarjeta', label: 'Tarjetas' },
    { value: 'Otros', label: 'Otros' },
] as const;

interface FormularioData {
    nroSuc: number;
    fechaEmision: string;
    codigoEntidad: number;
    descripcion: string;
    aclaracion: string;
    // tipoMovimiento se envía como código STRING al endpoint /api/TesoreriaCaja/movimientos-caja.
    // Para Movimientos Varios:
    //   "5"  -> Ingreso Varios
    //   "7"  -> Egreso Varios
    // Para Movimientos Bancarios:
    //   "55" -> Depósito
    //   "56" -> Extracción
    tipoMovimiento: string;
}

interface TipoPagoData {
    tipoPago: string; // "Cheque" o "Tarjeta"
    importe: number;
    detalle: string;
    mcp?: string;
    grupoMcp?: number;
    // Campos para Cheques
    nroCarga?: number; // cheque de cartera
    banco?: string;
    nroBanco?: number;
    nroSucursal?: number;
    nroCheque?: string;
    fechaEmision?: string;
    fechaVencimiento?: string;
    ctaBancaria?: string;
    librador?: string;
    nroLibrador?: string;
    concepto?: string;
    // Campos para Tarjetas
    codigoMarca?: number;
    nroTarjeta?: string;
    nroComprobanteTa?: number;
    fecha?: string;
    fechaPresentar?: string;
    numeroroAutorizacion?: string;
    telefono?: string;
    titularTarjeta?: string;
}

interface Sucursal {
    nroSuc: number;
    nombreSucursal: string;
}

const TIPO_PAGO_ICONS: Record<string, React.ReactNode> = {
    Efectivo: <AttachMoneyIcon fontSize="small" />,
    Transferencia: <AccountBalanceIcon fontSize="small" />,
    Cheque: <ReceiptIcon fontSize="small" />,
    Tarjeta: <CreditCardIcon fontSize="small" />,
    Otros: <MoreHorizIcon fontSize="small" />,
};

const resolveTipoPreseleccionado = (
    origen: 'varios' | 'bancario',
    tipoParam: string | null,
    state: { tipo?: string; tab?: number } | undefined,
): string | null => {
    const codIngreso = origen === 'bancario' ? '55' : '5';
    const codEgreso = origen === 'bancario' ? '56' : '7';
    const ingresoKeys = origen === 'bancario' ? ['deposito'] : ['ingreso'];
    const egresoKeys = origen === 'bancario' ? ['extraccion'] : ['egreso'];

    if (tipoParam && ingresoKeys.includes(tipoParam)) return codIngreso;
    if (tipoParam && egresoKeys.includes(tipoParam)) return codEgreso;
    if (state?.tipo && ingresoKeys.includes(state.tipo)) return codIngreso;
    if (state?.tipo && egresoKeys.includes(state.tipo)) return codEgreso;
    if (state?.tab === 1) return codIngreso;
    if (state?.tab === 2) return codEgreso;
    return null;
};

const initialPago: TipoPagoData = {
    tipoPago: '',
    importe: 0,
    detalle: '',
    fechaEmision: new Date().toISOString().split('T')[0],
    fechaVencimiento: new Date().toISOString().split('T')[0],
    fecha: new Date().toISOString().split('T')[0],
    fechaPresentar: new Date().toISOString().split('T')[0],
};

const NuevoMovimientoCaja: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const loggedUsername = user?.username || localStorage.getItem('userName') || localStorage.getItem('username') || localStorage.getItem('user') || 'Sistema';

    const navigationState = (location.state || {}) as { origen?: string; tipo?: string; tab?: number } | undefined;
    const origenMovimiento = navigationState?.origen === 'bancario' ? 'bancario' : 'varios';

    const COD_INGRESO = origenMovimiento === 'bancario' ? '55' : '5';
    const COD_EGRESO = origenMovimiento === 'bancario' ? '56' : '7';
    const LABEL_INGRESO = origenMovimiento === 'bancario' ? 'Depósito' : 'Ingreso';
    const LABEL_EGRESO = origenMovimiento === 'bancario' ? 'Extracción' : 'Egreso';
    const esMovimientoBancario = origenMovimiento === 'bancario';
    const listadoPath = esMovimientoBancario ? '/tesoreria/movimientos-bancarios' : '/tesoreria/movimientos-varios';
    const listadoLabel = esMovimientoBancario ? 'Movimientos Bancarios' : 'Movimientos Varios';

    const tipoPreseleccionado = useMemo(
        () => resolveTipoPreseleccionado(origenMovimiento, searchParams.get('tipo'), navigationState),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const [formulario, setFormulario] = useState<FormularioData>({
        nroSuc: Number(localStorage.getItem('nroSuc') || localStorage.getItem('sucursal') || 0) || 0,
        fechaEmision: new Date().toISOString().split('T')[0],
        codigoEntidad: 0,
        descripcion: '',
        aclaracion: '',
        tipoMovimiento: tipoPreseleccionado || '',
    });

    const [tipoMovimientoInicial, setTipoMovimientoInicial] = useState<string>(tipoPreseleccionado || '');
    const [tipoMovimientoConfirmado, setTipoMovimientoConfirmado] = useState(!!tipoPreseleccionado);
    const [saving, setSaving] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [openConfirmSalir, setOpenConfirmSalir] = useState(false);
    const motivoRef = useRef<HTMLDivElement>(null);
    const sucursalRef = useRef<HTMLDivElement>(null);

    const [detallesPago, setDetallesPago] = useState<TipoPagoData[]>([]);
    const [openPagoDialog, setOpenPagoDialog] = useState(false);
    const [pagoActual, setPagoActual] = useState<TipoPagoData>(initialPago);
    const [tipoPagoSeleccionado, setTipoPagoSeleccionado] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const [openImputableSearch, setOpenImputableSearch] = useState(false);
    const [imputableSeleccionado, setImputableSeleccionado] = useState<Imputable | null>(null);
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [loadingSucursales, setLoadingSucursales] = useState(false);

    const [grupoCajaSeleccionado, setGrupoCajaSeleccionado] = useState<GrupoCajaChica | null>(null);
    const [openGrupoCajaDialog, setOpenGrupoCajaDialog] = useState(false);
    const [gruposCaja, setGruposCaja] = useState<GrupoCajaChica[]>([]);
    const [grupoCajaLoading, setGrupoCajaLoading] = useState(false);
    const [grupoCajaSeleccionadoId, setGrupoCajaSeleccionadoId] = useState<number | null>(null);
    const [grupoCajaPage, setGrupoCajaPage] = useState(1);
    const [grupoCajaHasMore, setGrupoCajaHasMore] = useState(true);
    const [abrirPagoTrasGrupo, setAbrirPagoTrasGrupo] = useState(false);
    const [mediosTransferencia, setMediosTransferencia] = useState<MedioTransferencia[]>([]);
    const [cuentaTransferSeleccionadaId, setCuentaTransferSeleccionadaId] = useState<string | null>(null);
    const [mediosEfectivo, setMediosEfectivo] = useState<MedioTransferencia[]>([]);
    const [efectivoSeleccionadoId, setEfectivoSeleccionadoId] = useState<number | null>(null);
    const [bancos, setBancos] = useState<Banco[]>([]);
    const [bancoSeleccionadoId, setBancoSeleccionadoId] = useState<number | null>(null);
    // Banco seleccionado específicamente para depósitos/extracciones bancarios (55/56)
    const [bancoDepositoSeleccionado, setBancoDepositoSeleccionado] = useState<Banco | null>(null);
    const [openBancoDialog, setOpenBancoDialog] = useState(false);
    // Si es 'codigoEntidad', el diálogo se abrió para elegir banco de cartera (55/56): mostrar solo NroBanco >= 1000
    const [openBancoDialogPara, setOpenBancoDialogPara] = useState<'codigoEntidad' | 'cheque' | null>(null);
    const [bancoSearch, setBancoSearch] = useState('');
    const [bancoPage, setBancoPage] = useState(1);
    const [bancoHasMore, setBancoHasMore] = useState(true);
    const [bancoLoading, setBancoLoading] = useState(false);
    const [mediosCheques, setMediosCheques] = useState<MedioTransferencia[]>([]);
    const [chequeSeleccionadoIdx, setChequeSeleccionadoIdx] = useState<string | null>(null);
    // Cheques de cartera (Egreso/Extracción, o Depósito bancario 55 + Cheques de Tercero)
    const [chequesCartera, setChequesCartera] = useState<ChequeCartera[]>([]);
    const [chequesCarteraSeleccionados, setChequesCarteraSeleccionados] = useState<Set<number>>(new Set());
    const [loadingChequesCartera, setLoadingChequesCartera] = useState(false);
    const [esChequeCartera, setEsChequeCartera] = useState(false);
    const [busquedaCheque, setBusquedaCheque] = useState('');
    const [orderBy, setOrderBy] = useState<keyof ChequeCartera | ''>('');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [mediosTarjeta, setMediosTarjeta] = useState<MedioTransferencia[]>([]);
    const [tarjetaSeleccionadaIdx, setTarjetaSeleccionadaIdx] = useState<string | null>(null);
    const [marcasTarjetas, setMarcasTarjetas] = useState<MarcaTarjeta[]>([]);
    // Cupones de cartera (Egreso/Extracción, o Depósito bancario 55 + Tarjetas)
    const [cuponesCartera, setCuponesCartera] = useState<CuponCartera[]>([]);
    const [cuponesCarteraSeleccionados, setCuponesCarteraSeleccionados] = useState<Set<number>>(new Set());
    const [loadingCuponesCartera, setLoadingCuponesCartera] = useState(false);
    const [esCuponCartera, setEsCuponCartera] = useState(false);
    const [busquedaCupon, setBusquedaCupon] = useState('');
    const [orderByCupon, setOrderByCupon] = useState<keyof CuponCartera | ''>('');
    const [orderCupon, setOrderCupon] = useState<'asc' | 'desc'>('asc');
    // Almacenar los objetos completos de medios de tarjeta desde la API
    const [_mediosTarjetaCompletos, setMediosTarjetaCompletos] = useState<any[]>([]);
    const [mediosOtros, setMediosOtros] = useState<MedioTransferencia[]>([]);
    const [otrosSeleccionadoIdx, setOtrosSeleccionadoIdx] = useState<string | null>(null);
    const [isMontoFocused, setIsMontoFocused] = useState(false);

    const isTabletOrLarger = useMediaQuery(theme.breakpoints.up('md'));

    const handleRequestSort = (property: keyof ChequeCartera) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const ordenarCheques = (cheques: ChequeCartera[]): ChequeCartera[] => {
        if (!orderBy) return cheques;
        return [...cheques].sort((a, b) => {
            let aValue: any = a[orderBy];
            let bValue: any = b[orderBy];
            if (aValue == null) aValue = '';
            if (bValue == null) bValue = '';
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return order === 'asc' ? aValue - bValue : bValue - aValue;
            }
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            if (order === 'asc') return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
            return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
        });
    };

    const handleSeleccionarTodos = (chequesFiltrados: ChequeCartera[]) => {
        const todosSeleccionados = chequesFiltrados.every(cheque => chequesCarteraSeleccionados.has(cheque.nroCarga));
        const nuevoSet = new Set(chequesCarteraSeleccionados);
        if (todosSeleccionados) {
            chequesFiltrados.forEach(cheque => nuevoSet.delete(cheque.nroCarga));
        } else {
            chequesFiltrados.forEach(cheque => nuevoSet.add(cheque.nroCarga));
        }
        setChequesCarteraSeleccionados(nuevoSet);
    };

    const totalImporteSeleccionado = useMemo(() => {
        return chequesCartera
            .filter(cheque => chequesCarteraSeleccionados.has(cheque.nroCarga))
            .reduce((total, cheque) => total + (cheque.importe || 0), 0);
    }, [chequesCartera, chequesCarteraSeleccionados]);

    const handleRequestSortCupon = (property: keyof CuponCartera) => {
        const isAsc = orderByCupon === property && orderCupon === 'asc';
        setOrderCupon(isAsc ? 'desc' : 'asc');
        setOrderByCupon(property);
    };

    const ordenarCupones = (cupones: CuponCartera[]): CuponCartera[] => {
        if (!orderByCupon) return cupones;
        return [...cupones].sort((a, b) => {
            let aValue: any = a[orderByCupon];
            let bValue: any = b[orderByCupon];
            if (aValue == null) aValue = '';
            if (bValue == null) bValue = '';
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return orderCupon === 'asc' ? aValue - bValue : bValue - aValue;
            }
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            if (orderCupon === 'asc') return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
            return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
        });
    };

    const handleSeleccionarTodosCupones = (cuponesFiltrados: CuponCartera[]) => {
        const todosSeleccionados = cuponesFiltrados.every(cupon => cuponesCarteraSeleccionados.has(cupon.nroCarga));
        const nuevoSet = new Set(cuponesCarteraSeleccionados);
        if (todosSeleccionados) {
            cuponesFiltrados.forEach(cupon => nuevoSet.delete(cupon.nroCarga));
        } else {
            cuponesFiltrados.forEach(cupon => nuevoSet.add(cupon.nroCarga));
        }
        setCuponesCarteraSeleccionados(nuevoSet);
    };

    const totalImporteCuponesSeleccionados = useMemo(() => {
        return cuponesCartera
            .filter(cupon => cuponesCarteraSeleccionados.has(cupon.nroCarga))
            .reduce((total, cupon) => total + (cupon.montoOperacion || 0), 0);
    }, [cuponesCartera, cuponesCarteraSeleccionados]);

    const nroCargasCuponesYaAgregados = useMemo(() => {
        const set = new Set<number>();
        detallesPago.forEach((p) => {
            if (p.tipoPago === 'Tarjeta' && p.nroCarga != null && p.nroCarga > 0) {
                set.add(p.nroCarga);
            }
        });
        return set;
    }, [detallesPago]);

    const cuponesCarteraDisponibles = useMemo(
        () => cuponesCartera.filter((c) => !nroCargasCuponesYaAgregados.has(c.nroCarga)),
        [cuponesCartera, nroCargasCuponesYaAgregados]
    );

    // Cargar sucursales desde /api/SucursalesVenta
    useEffect(() => {
        const cargarSucursales = async () => {
            setLoadingSucursales(true);
            try {
                const response = await api.get('/api/SucursalesVenta');
                const data = response.data;

                // La respuesta puede venir como { items: [...] } o como array directo
                if (data && Array.isArray((data as any).items)) {
                    setSucursales((data as any).items);
                } else if (Array.isArray(data)) {
                    setSucursales(data);
                } else {
                    setSucursales([]);
                }
            } catch (error: any) {
                console.error('Error al cargar sucursales de venta:', error);
                enqueueSnackbar('Error al cargar sucursales', { variant: 'error' });
                setSucursales([]);
            } finally {
                setLoadingSucursales(false);
            }
        };
        cargarSucursales();
    }, [enqueueSnackbar]);

    const handleGrupoCajaChange = (grupo: GrupoCajaChica | null) => {
        if (detallesPago.length > 0) return;
        setGrupoCajaSeleccionado(grupo);
        setGrupoCajaSeleccionadoId(grupo?.id ?? null);
        setPagoActual((prev) => ({ ...prev, grupoMcp: grupo?.codigo }));
    };

    const grupoCajaBloqueado = detallesPago.length > 0;

    useEffect(() => {
        if (!openGrupoCajaDialog) return;
        let cancelled = false;
        const PAGE_SIZE = 30;
        const loadFirstPage = async () => {
            try {
                setGrupoCajaLoading(true);
                const data = await tesoreriaService.getGruposCajaChicaPaged({
                    usuario: loggedUsername,
                    pageNumber: 1,
                    pageSize: PAGE_SIZE,
                });
                if (cancelled) return;
                setGruposCaja(data || []);
                setGrupoCajaPage(2);
                setGrupoCajaHasMore((data?.length || 0) >= PAGE_SIZE);
                if (grupoCajaSeleccionado?.id) {
                    setGrupoCajaSeleccionadoId(grupoCajaSeleccionado.id);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Error al cargar grupos de caja paginados', error);
                    enqueueSnackbar('Error al cargar grupos de caja', { variant: 'error' });
                }
            } finally {
                if (!cancelled) setGrupoCajaLoading(false);
            }
        };
        loadFirstPage();
        return () => {
            cancelled = true;
        };
    }, [openGrupoCajaDialog, loggedUsername, enqueueSnackbar, grupoCajaSeleccionado?.id]);

    const handleGrupoCajaScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
        if (!grupoCajaHasMore || grupoCajaLoading) return;
        const target = e.currentTarget;
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 64) {
            const PAGE_SIZE = 30;
            void (async () => {
                try {
                    setGrupoCajaLoading(true);
                    const data = await tesoreriaService.getGruposCajaChicaPaged({
                        usuario: loggedUsername,
                        pageNumber: grupoCajaPage,
                        pageSize: PAGE_SIZE,
                    });
                    const existingIds = new Set(gruposCaja.map((g) => g.id));
                    const nuevos = (data || []).filter((g) => !existingIds.has(g.id));
                    setGrupoCajaHasMore(nuevos.length > 0);
                    if (nuevos.length > 0) {
                        setGrupoCajaPage((prev) => prev + 1);
                        setGruposCaja((prev) => [...prev, ...nuevos]);
                    }
                } finally {
                    setGrupoCajaLoading(false);
                }
            })();
        }
    };

    const handleConfirmarGrupoCaja = () => {
        if (grupoCajaSeleccionadoId == null) return;
        const selected = gruposCaja.find((g) => g.id === grupoCajaSeleccionadoId);
        if (!selected) return;
        persistGrupoCajaChica(selected);
        setGrupoCajaSeleccionado(selected);
        setPagoActual((prev) => ({ ...prev, grupoMcp: selected.codigo }));
        setOpenGrupoCajaDialog(false);
        if (abrirPagoTrasGrupo) {
            setAbrirPagoTrasGrupo(false);
            setTimeout(() => {
                setPagoActual(initialPago);
                setTipoPagoSeleccionado('');
                setActiveStep(0);
                setOpenPagoDialog(true);
            }, 0);
        }
    };

    // Cargar bancos en diálogo al abrir o al buscar (igual que en carga de recibos)
    const BANCO_PAGE_SIZE = 20; // Límite del backend (1-20)
    useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;

        const fetchPage = async (reset: boolean) => {
            try {
                setBancoLoading(true);
                const pageToLoad = reset ? 1 : bancoPage;
                const items = await tesoreriaService.getBancosPaged({
                    searchTerm: bancoSearch || undefined,
                    pageNumber: pageToLoad,
                    pageSize: BANCO_PAGE_SIZE,
                });
                if (cancelled) return;
                setBancoHasMore(items.length >= BANCO_PAGE_SIZE);
                setBancoPage(pageToLoad + 1);
                setBancos(prev => {
                    const next = reset ? items : [...prev, ...items];
                    const map = new Map<number, Banco>();
                    for (const b of next) if (!map.has(b.id)) map.set(b.id, b);
                    return Array.from(map.values());
                });
            } catch (e) {
                if (!cancelled) {
                    console.error('No se pudieron cargar los bancos', e);
                    setBancos([]);
                }
            } finally {
                if (!cancelled) setBancoLoading(false);
            }
        };

        const fetchAllPagesForCartera = async () => {
            try {
                setBancoLoading(true);
                const mapById = new Map<number, Banco>();
                let pageNumber = 1;
                let hasMore = true;
                while (hasMore && !cancelled) {
                    const items = await tesoreriaService.getCarteraBancosPaged({
                        searchTerm: bancoSearch || undefined,
                        pageNumber,
                        pageSize: BANCO_PAGE_SIZE,
                    });
                    if (cancelled) return;
                    for (const b of items) if (!mapById.has(b.id)) mapById.set(b.id, b);
                    if (items.length < BANCO_PAGE_SIZE) hasMore = false;
                    else pageNumber += 1;
                    if (!cancelled) setBancos(Array.from(mapById.values()));
                }
                if (!cancelled) {
                    setBancoHasMore(false);
                    setBancoPage(pageNumber + 1);
                }
            } catch (e) {
                if (!cancelled) {
                    console.error('No se pudieron cargar los bancos de cartera', e);
                    setBancos([]);
                }
            } finally {
                if (!cancelled) setBancoLoading(false);
            }
        };

        if (openBancoDialog) {
            if (openBancoDialogPara === 'codigoEntidad') {
                timer = setTimeout(() => fetchAllPagesForCartera(), 300);
            } else {
                timer = setTimeout(() => fetchPage(true), 300);
            }
        }
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [openBancoDialog, openBancoDialogPara, bancoSearch]);

    const handleBancoScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
        if (!bancoHasMore || bancoLoading) return;
        const target = e.currentTarget;
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 64) {
            (async () => {
                try {
                    setBancoLoading(true);
                    const items = await tesoreriaService.getBancosPaged({
                        searchTerm: bancoSearch || undefined,
                        pageNumber: bancoPage,
                        pageSize: BANCO_PAGE_SIZE,
                    });
                    setBancoHasMore(items.length >= BANCO_PAGE_SIZE);
                    setBancoPage(prev => prev + 1);
                    setBancos(prev => {
                        const next = [...prev, ...items];
                        const map = new Map<number, Banco>();
                        for (const b of next) if (!map.has(b.id)) map.set(b.id, b);
                        return Array.from(map.values());
                    });
                } finally {
                    setBancoLoading(false);
                }
            })();
        }
    };

    // Cargar medios cuando se selecciona un grupo de caja
    useEffect(() => {
        const cargarMedios = async () => {
            if (grupoCajaSeleccionado?.codigo) {
                try {
                    const [
                        mediosChequesData,
                        mediosTransferenciaData,
                        mediosEfectivoData,
                        mediosOtrosData,
                        responseTarjeta
                    ] = await Promise.all([
                        tesoreriaService.getMediosChequesPorGrupo(grupoCajaSeleccionado.codigo),
                        tesoreriaService.getMediosTransferenciaPorGrupo(grupoCajaSeleccionado.codigo),
                        tesoreriaService.getMediosEfectivoPorGrupo(grupoCajaSeleccionado.codigo),
                        tesoreriaService.getMediosOtrosPorGrupo(grupoCajaSeleccionado.codigo),
                        api.get<any>(`/api/Mcps/por-grupo/${grupoCajaSeleccionado.codigo}`, {
                            headers: {
                                Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : undefined,
                            },
                            params: { pageNumber: 1, pageSize: 1000 },
                        }),
                    ]);
                    
                    setMediosCheques(mediosChequesData);
                    setMediosTransferencia(mediosTransferenciaData);
                    setMediosEfectivo(mediosEfectivoData);
                    setMediosOtros(mediosOtrosData);
                    
                    // Procesar medios de tarjeta
                    const data = responseTarjeta.data;
                    const items: any[] = Array.isArray(data)
                        ? data
                        : Array.isArray(data?.items)
                        ? data.items
                        : Array.isArray(data?.value)
                        ? data.value
                        : [];

                    const filtrados = items.filter((it: any) => {
                        const rawTipo = ((it?.tipo ?? it?.Tipo ?? '') + '').trim().toUpperCase();
                        const rawClase = ((it?.clase ?? it?.Clase ?? '') + '').trim().toUpperCase();
                        const rawNombre = ((it?.nombre ?? it?.banco ?? it?.descripcion ?? it?.detalle ?? '') + '')
                            .trim()
                            .toUpperCase();
                        const looksLikeTarjeta =
                            rawTipo === 'T' ||
                            rawTipo === 'TA' ||
                            rawTipo === 'TJ' ||
                            rawTipo === 'TC' ||
                            rawTipo === 'TD' ||
                            rawTipo.startsWith('TARJ') ||
                            rawClase.includes('TARJ') ||
                            rawNombre.includes('TARJ');
                        return looksLikeTarjeta;
                    });

                    // Guardar objetos completos (aunque no los usaremos para tarjetas, igual que en NuevaOrdenPago)
                    setMediosTarjetaCompletos(filtrados);
                    
                    // Mapear a MedioTransferencia para la UI
                    const mediosTarjetaData = filtrados.map((it: any, idx: number) => ({
                        id: it?.id ?? it?.codigo ?? it?.nroCuenta ?? it?.cartera ?? idx,
                        nombre: it?.banco ?? it?.nombre ?? it?.descripcion ?? it?.detalle ?? `Medio ${idx + 1}`,
                        descripcion: it?.descripcion ?? it?.Detalle ?? undefined,
                        mcp: it?.mcp ?? it?.MCP ?? undefined,
                        clase: it?.clase ?? it?.Clase ?? undefined,
                        grupo: it?.grupoMcp ?? it?.grupo ?? undefined,
                        nroSuc: it?.nroSuc ?? it?.sucursal ?? undefined,
                        tipo: it?.tipo ?? it?.Tipo ?? undefined,
                    }));
                    setMediosTarjeta(mediosTarjetaData);
                } catch (error: any) {
                    console.error('Error al cargar medios:', error);
                    setMediosCheques([]);
                    setMediosTransferencia([]);
                    setMediosEfectivo([]);
                    setMediosOtros([]);
                    setMediosTarjeta([]);
                    setMediosTarjetaCompletos([]);
                }
            } else {
                setMediosCheques([]);
                setMediosTransferencia([]);
                setMediosEfectivo([]);
                setMediosOtros([]);
                setMediosTarjeta([]);
                setMediosTarjetaCompletos([]);
            }
        };
        cargarMedios();
    }, [grupoCajaSeleccionado]);

    // Cargar marcas de tarjeta al seleccionar tipo Tarjeta en el diálogo de pago (solo alta manual)
    useEffect(() => {
        const usaSeleccionCuponesCartera =
            tipoMovimientoInicial === COD_EGRESO ||
            (origenMovimiento === 'bancario' && tipoMovimientoInicial === COD_INGRESO);

        if (openPagoDialog && tipoPagoSeleccionado === 'Tarjeta' && !usaSeleccionCuponesCartera) {
            tesoreriaService.getMarcasTarjetas()
                .then((marcas) => setMarcasTarjetas(marcas || []))
                .catch(() => setMarcasTarjetas([]));
        } else {
            setMarcasTarjetas([]);
        }
    }, [openPagoDialog, tipoPagoSeleccionado, tipoMovimientoInicial, COD_EGRESO, COD_INGRESO, origenMovimiento]);

    // Cargar cupones de cartera cuando es Egreso/Extracción o Depósito bancario (55) + Tarjetas
    useEffect(() => {
        const usaSeleccionCuponesCartera =
            tipoMovimientoInicial === COD_EGRESO ||
            (origenMovimiento === 'bancario' && tipoMovimientoInicial === COD_INGRESO);

        if (usaSeleccionCuponesCartera && tipoPagoSeleccionado === 'Tarjeta' && tarjetaSeleccionadaIdx != null && openPagoDialog && grupoCajaSeleccionado) {
            setEsCuponCartera(true);
            setCuponesCarteraSeleccionados(new Set());
            setLoadingCuponesCartera(true);
            const numeroGrupo = grupoCajaSeleccionado.codigo ?? grupoCajaSeleccionado.id;
            if (numeroGrupo == null) {
                setLoadingCuponesCartera(false);
                return;
            }
            let cancelled = false;
            const idxMedio = Number(tarjetaSeleccionadaIdx);
            const medioSel = !isNaN(idxMedio) && idxMedio >= 0 && idxMedio < mediosTarjeta.length
                ? mediosTarjeta[idxMedio]
                : null;
            const mcpFiltro = (medioSel?.mcp ?? '').trim();
            (async () => {
                try {
                    const cajaAbierta = await tesoreriaService.getCajaAbiertaPorGrupo(numeroGrupo);
                    if (cancelled) return;
                    const codigoCaja = cajaAbierta?.codigoCaja ?? cajaAbierta?.nroCaja ?? cajaAbierta?.codigo ?? cajaAbierta?.id ?? null;
                    if (cajaAbierta && codigoCaja != null && codigoCaja > 0) {
                        const PAGE_SIZE = 20;
                        let pageNumber = 1;
                        let allCupones: CuponCartera[] = [];
                        let hasMore = true;
                        const filtros = mcpFiltro ? { mcp: mcpFiltro } : undefined;
                        while (hasMore) {
                            const result = await tesoreriaService.getTarjetasCartera(codigoCaja, pageNumber, PAGE_SIZE, filtros);
                            if (cancelled) return;
                            allCupones = [...allCupones, ...result.cupones];
                            hasMore = result.hasMore && result.cupones.length > 0;
                            if (!hasMore) break;
                            pageNumber++;
                            if (pageNumber > 100) break;
                        }
                        setCuponesCartera(allCupones);
                    } else {
                        enqueueSnackbar('No se encontró una caja abierta para este grupo', { variant: 'warning' });
                        setCuponesCartera([]);
                    }
                } catch (error) {
                    if (cancelled) return;
                    console.error('Error al cargar cupones de cartera:', error);
                    enqueueSnackbar('Error al cargar cupones de cartera', { variant: 'error' });
                    setCuponesCartera([]);
                } finally {
                    if (!cancelled) setLoadingCuponesCartera(false);
                }
            })();
            return () => { cancelled = true; };
        } else {
            setEsCuponCartera(false);
            setCuponesCartera([]);
            setCuponesCarteraSeleccionados(new Set());
        }
    }, [tipoMovimientoInicial, tipoPagoSeleccionado, tarjetaSeleccionadaIdx, openPagoDialog, grupoCajaSeleccionado, mediosTarjeta, enqueueSnackbar, COD_EGRESO, COD_INGRESO, origenMovimiento]);

    // Cargar cheques de cartera cuando es Egreso/Extracción (COD_EGRESO) o Depósito bancario (55)
    // + Cheques de Tercero y hay medio específico seleccionado
    useEffect(() => {
        const usaSeleccionChequesCartera =
            tipoMovimientoInicial === COD_EGRESO ||
            (origenMovimiento === 'bancario' && tipoMovimientoInicial === COD_INGRESO);

        if (usaSeleccionChequesCartera && tipoPagoSeleccionado === 'Cheque' && chequeSeleccionadoIdx != null && openPagoDialog && grupoCajaSeleccionado) {
            setEsChequeCartera(true);
            setLoadingChequesCartera(true);
            const numeroGrupo = grupoCajaSeleccionado.codigo ?? grupoCajaSeleccionado.id;
            if (numeroGrupo == null) {
                setLoadingChequesCartera(false);
                return;
            }
            (async () => {
                try {
                    const cajaAbierta = await tesoreriaService.getCajaAbiertaPorGrupo(numeroGrupo);
                    const codigoCaja = cajaAbierta?.codigoCaja ?? cajaAbierta?.nroCaja ?? cajaAbierta?.codigo ?? cajaAbierta?.id ?? null;
                    if (cajaAbierta && codigoCaja != null && codigoCaja > 0) {
                        const PAGE_SIZE = 20;
                        let pageNumber = 1;
                        let allCheques: ChequeCartera[] = [];
                        let hasMore = true;
                        while (hasMore) {
                            const result = await tesoreriaService.getChequesCartera(codigoCaja, pageNumber, PAGE_SIZE);
                            allCheques = [...allCheques, ...result.cheques];
                            hasMore = result.hasMore && result.cheques.length > 0;
                            if (!hasMore) break;
                            pageNumber++;
                            if (pageNumber > 100) break;
                        }
                        setChequesCartera(allCheques);
                    } else {
                        enqueueSnackbar('No se encontró una caja abierta para este grupo', { variant: 'warning' });
                        setChequesCartera([]);
                    }
                } catch (error) {
                    console.error('Error al cargar cheques de cartera:', error);
                    enqueueSnackbar('Error al cargar cheques de cartera', { variant: 'error' });
                    setChequesCartera([]);
                } finally {
                    setLoadingChequesCartera(false);
                }
            })();
        } else {
            setEsChequeCartera(false);
            setChequesCartera([]);
            setChequesCarteraSeleccionados(new Set());
        }
    }, [tipoMovimientoInicial, tipoPagoSeleccionado, chequeSeleccionadoIdx, openPagoDialog, grupoCajaSeleccionado, enqueueSnackbar, COD_EGRESO, COD_INGRESO, origenMovimiento]);

    const handleFormularioChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormulario(prev => ({ ...prev, [name]: value }));
    };

    const handleImputableSelect = (imputable: Imputable) => {
        setImputableSeleccionado(imputable);
        setFormulario(prev => {
            const tipoActual = (prev.tipoMovimiento || tipoMovimientoInicial || COD_INGRESO).toString();
            const esMovimientoBancario =
                origenMovimiento === 'bancario' && (tipoActual === COD_INGRESO || tipoActual === COD_EGRESO);

            return {
                ...prev,
                // En depósito (55) o extracción (56) bancaria, CodigoEntidad debe ser el NroBanco de cartera,
                // por lo que NO lo sobreescribimos con la cuenta imputable.
                codigoEntidad: esMovimientoBancario ? prev.codigoEntidad : imputable.nroCuenta,
                descripcion: imputable.nomCuenta || '',
            };
        });
        setOpenImputableSearch(false);
    };

    const handleOpenPagoDialog = () => {
        if (!esMovimientoBancario && !imputableSeleccionado) {
            setShowValidation(true);
            enqueueSnackbar('Debe seleccionar un concepto primero', { variant: 'warning' });
            scrollToFirstError();
            return;
        }
        if (!grupoCajaSeleccionado) {
            enqueueSnackbar('Debe seleccionar un Grupo de Caja primero', { variant: 'warning' });
            setAbrirPagoTrasGrupo(true);
            setOpenGrupoCajaDialog(true);
            return;
        }
        setPagoActual(initialPago);
        setTipoPagoSeleccionado('');
        setActiveStep(0);
        setOpenPagoDialog(true);
    };

    const handleClosePagoDialog = () => {
        setOpenPagoDialog(false);
        setPagoActual(initialPago);
        setTipoPagoSeleccionado('');
        setActiveStep(0);
        setBancoSeleccionadoId(null);
        setChequeSeleccionadoIdx(null);
        setEfectivoSeleccionadoId(null);
        setCuentaTransferSeleccionadaId(null);
        setTarjetaSeleccionadaIdx(null);
        setOtrosSeleccionadoIdx(null);
        setEsChequeCartera(false);
        setChequesCartera([]);
        setChequesCarteraSeleccionados(new Set());
        setBusquedaCheque('');
        setOrderBy('');
        setOrder('asc');
        setLoadingChequesCartera(false);
        setEsCuponCartera(false);
        setCuponesCartera([]);
        setCuponesCarteraSeleccionados(new Set());
        setBusquedaCupon('');
        setOrderByCupon('');
        setOrderCupon('asc');
        setLoadingCuponesCartera(false);
    };

    const handleTipoPagoChange = (tipo: string) => {
        setTipoPagoSeleccionado(tipo);
        setPagoActual(prev => ({ ...prev, tipoPago: tipo }));
        setActiveStep(1);
    };

    const handlePagoFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setPagoActual(prev => ({ ...prev, [name]: value }));
    };

    const handleChequeMedioChange = (idx: string | null) => {
        setChequeSeleccionadoIdx(idx ?? '');
        if (!idx) return;
        const medio = mediosCheques.find((_, i) => String(i) === idx);
        if (medio) {
            setPagoActual(prev => ({ ...prev, mcp: medio.mcp || '', grupoMcp: medio.grupo }));
        }
    };

    const handleAgregarPago = () => {
        if (!grupoCajaSeleccionado) {
            enqueueSnackbar('Debe seleccionar un Grupo de Caja primero', { variant: 'warning' });
            setAbrirPagoTrasGrupo(false);
            setOpenGrupoCajaDialog(true);
            return;
        }

        if (pagoActual.tipoPago === 'Cheque' && esChequeCartera) {
            if (chequesCarteraSeleccionados.size === 0) {
                enqueueSnackbar('Debe seleccionar al menos un cheque de cartera', { variant: 'warning' });
                return;
            }
            const chequesSeleccionados = chequesCartera.filter(c => chequesCarteraSeleccionados.has(c.nroCarga));
            const limpiarYLimitar = (texto: string | undefined, maxLength: number): string => (!texto ? '' : texto.trim().slice(0, maxLength));
            const nuevosPagos: TipoPagoData[] = chequesSeleccionados.map(cheque => {
                const fechaEmision = cheque.fechaEmision ? new Date(cheque.fechaEmision).toISOString().split('T')[0] : '';
                const fechaVencimiento = cheque.fechaVencimiento ? new Date(cheque.fechaVencimiento).toISOString().split('T')[0] : '';
                return {
                    ...pagoActual,
                    tipoPago: 'Cheque',
                    nroCarga: cheque.nroCarga,
                    nroBanco: cheque.nroBanco,
                    banco: cheque.nombreBanco,
                    nroSucursal: cheque.nroSucursal,
                    nroCheque: String(cheque.nroCheque),
                    importe: cheque.importe,
                    fechaEmision,
                    fechaVencimiento,
                    ctaBancaria: limpiarYLimitar(cheque.nroCuenta, 20),
                    librador: limpiarYLimitar(cheque.librador, 25),
                    nroLibrador: limpiarYLimitar(cheque.nroLibrador, 20),
                    concepto: limpiarYLimitar(cheque.concepto, 255),
                    grupoMcp: grupoCajaSeleccionado.codigo,
                };
            });
            setDetallesPago(prev => [...prev, ...nuevosPagos]);
            handleClosePagoDialog();
            return;
        }

        if (pagoActual.tipoPago === 'Tarjeta' && esCuponCartera) {
            if (mediosTarjeta.length > 0 && !tarjetaSeleccionadaIdx) {
                enqueueSnackbar('Debe seleccionar un medio de tarjeta', { variant: 'warning' });
                return;
            }
            if (cuponesCarteraSeleccionados.size === 0) {
                enqueueSnackbar('Debe seleccionar al menos un cupón de cartera', { variant: 'warning' });
                return;
            }
            const cuponesSeleccionados = cuponesCarteraDisponibles.filter(c => cuponesCarteraSeleccionados.has(c.nroCarga));
            const duplicados = cuponesSeleccionados.filter(c => nroCargasCuponesYaAgregados.has(c.nroCarga));
            if (duplicados.length > 0) {
                enqueueSnackbar('Uno o más cupones seleccionados ya fueron agregados al movimiento', { variant: 'warning' });
                return;
            }
            const sinMonto = cuponesSeleccionados.filter(c => !c.montoOperacion || c.montoOperacion <= 0);
            if (sinMonto.length > 0) {
                enqueueSnackbar('Uno o más cupones seleccionados no tienen monto válido', { variant: 'warning' });
                return;
            }
            const nuevosPagos: TipoPagoData[] = cuponesSeleccionados.map(cupon => {
                const fecha = cupon.fecha ? new Date(cupon.fecha).toISOString().split('T')[0] : '';
                const fechaPresentar = cupon.fechaPresentar ? new Date(cupon.fechaPresentar).toISOString().split('T')[0] : '';
                return {
                    ...pagoActual,
                    tipoPago: 'Tarjeta',
                    nroCarga: cupon.nroCarga,
                    codigoMarca: cupon.codigoMarca,
                    nroTarjeta: cupon.nroTarjeta,
                    nroComprobanteTa: cupon.nroComprobanteTa,
                    numeroroAutorizacion: cupon.numeroroAutorizacion,
                    fecha,
                    fechaPresentar,
                    titularTarjeta: cupon.titularTarjeta,
                    telefono: cupon.telefono,
                    importe: cupon.montoOperacion,
                    grupoMcp: grupoCajaSeleccionado.codigo,
                };
            });
            setDetallesPago(prev => [...prev, ...nuevosPagos]);
            handleClosePagoDialog();
            return;
        }

        if (!pagoActual.tipoPago || !pagoActual.importe || pagoActual.importe <= 0) {
            enqueueSnackbar('Debe completar el tipo de pago y el importe', { variant: 'warning' });
            return;
        }

        // Validaciones según el tipo de pago
        if (pagoActual.tipoPago === 'Efectivo') {
            if (!pagoActual.mcp) {
                enqueueSnackbar('Debe seleccionar un medio de efectivo', { variant: 'warning' });
                return;
            }
        } else if (pagoActual.tipoPago === 'Transferencia') {
            if (!cuentaTransferSeleccionadaId || !pagoActual.mcp) {
                enqueueSnackbar('Debe seleccionar un medio de transferencia', { variant: 'warning' });
                return;
            }
        } else if (pagoActual.tipoPago === 'Cheque') {
            if (!esChequeCartera && (!pagoActual.mcp || !pagoActual.nroBanco || !pagoActual.nroSucursal || !pagoActual.nroCheque || !pagoActual.librador || !pagoActual.nroLibrador)) {
                enqueueSnackbar('Debe completar todos los campos del cheque (incluido CUIT del librador)', { variant: 'warning' });
                return;
            }
        } else if (pagoActual.tipoPago === 'Tarjeta' && !esCuponCartera) {
            if (mediosTarjeta.length > 0 && !tarjetaSeleccionadaIdx) {
                enqueueSnackbar('Debe seleccionar un medio de tarjeta', { variant: 'warning' });
                return;
            }
            if (!pagoActual.mcp) {
                enqueueSnackbar('Debe seleccionar un medio de cobro', { variant: 'warning' });
                return;
            }
            if (!pagoActual.codigoMarca || pagoActual.codigoMarca <= 0) {
                enqueueSnackbar('Debe seleccionar la Marca de tarjeta', { variant: 'warning' });
                return;
            }
            if (!pagoActual.nroTarjeta || String(pagoActual.nroTarjeta).trim() === '') {
                enqueueSnackbar('Debe ingresar el N° de Tarjeta', { variant: 'warning' });
                return;
            }
            const nroComp = Number(pagoActual.nroComprobanteTa);
            if (!Number.isFinite(nroComp) || nroComp <= 0) {
                enqueueSnackbar('Debe ingresar el N° Comprobante', { variant: 'warning' });
                return;
            }
            if (!pagoActual.fecha) {
                enqueueSnackbar('Debe ingresar la Fecha', { variant: 'warning' });
                return;
            }
            if (!pagoActual.numeroroAutorizacion || String(pagoActual.numeroroAutorizacion).trim() === '') {
                enqueueSnackbar('Debe ingresar el N° Autorización/Lote', { variant: 'warning' });
                return;
            }
        } else if (pagoActual.tipoPago === 'Otros') {
            if (mediosOtros.length > 0 && !otrosSeleccionadoIdx) {
                enqueueSnackbar('Debe seleccionar un medio', { variant: 'warning' });
                return;
            }
            if (!pagoActual.mcp) {
                enqueueSnackbar('Debe seleccionar un medio de cobro', { variant: 'warning' });
                return;
            }
        }

        setPagoActual(prev => ({ ...prev, grupoMcp: grupoCajaSeleccionado.codigo }));
        const importeNum = truncToDecimals(pagoActual.importe);
        setDetallesPago(prev => [...prev, { ...pagoActual, importe: importeNum, grupoMcp: grupoCajaSeleccionado.codigo }]);
        handleClosePagoDialog();
    };

    const removePago = (index: number) => {
        setDetallesPago(prev => prev.filter((_, i) => i !== index));
    };

    const totalMedios = detallesPago.reduce((sum, p) => sum + (Number(p.importe) || 0), 0);

    const tipoActual = (formulario.tipoMovimiento || tipoMovimientoInicial || COD_INGRESO).toString();
    const esEgreso = tipoActual === COD_EGRESO;

    const totalesPorTipo = useMemo(() => {
        const map = new Map<string, number>();
        detallesPago.forEach((p) => {
            const t = p.tipoPago || 'Otros';
            map.set(t, (map.get(t) || 0) + (Number(p.importe) || 0));
        });
        return map;
    }, [detallesPago]);

    const pageTitle = useMemo(() => {
        if (!tipoMovimientoConfirmado) {
            return esMovimientoBancario ? 'Nuevo movimiento bancario' : 'Nuevo movimiento varios';
        }
        if (esMovimientoBancario) {
            return esEgreso ? 'Nueva extracción bancaria' : 'Nuevo depósito bancario';
        }
        return esEgreso ? 'Nuevo egreso varios' : 'Nuevo ingreso varios';
    }, [tipoMovimientoConfirmado, esMovimientoBancario, esEgreso]);

    const isDirty = useMemo(
        () =>
            !!imputableSeleccionado ||
            detallesPago.length > 0 ||
            !!formulario.aclaracion.trim() ||
            (!!tipoMovimientoConfirmado && !!tipoMovimientoInicial),
        [imputableSeleccionado, detallesPago.length, formulario.aclaracion, tipoMovimientoConfirmado, tipoMovimientoInicial],
    );

    const motivoValido = esMovimientoBancario || !!imputableSeleccionado;
    const sucursalValida = formulario.nroSuc > 0;
    const mediosValidos = detallesPago.length > 0 && totalMedios > 0;
    const grupoCajaValido = !!grupoCajaSeleccionado?.codigo;
    const bancoValido = !esMovimientoBancario || (Number(formulario.codigoEntidad) > 0);
    const formularioListo = motivoValido && sucursalValida && mediosValidos && grupoCajaValido && tipoMovimientoConfirmado && bancoValido;

    const handleVolver = useCallback(() => {
        if (isDirty) {
            setOpenConfirmSalir(true);
        } else {
            navigate(listadoPath);
        }
    }, [isDirty, navigate, listadoPath]);

    const handleSelectTipo = (tipo: string) => {
        setTipoMovimientoInicial(tipo);
        setFormulario((prev) => ({
            ...prev,
            tipoMovimiento: tipo,
        }));
        setTipoMovimientoConfirmado(true);
    };

    const scrollToFirstError = () => {
        if (!esMovimientoBancario && !imputableSeleccionado && motivoRef.current) {
            motivoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        if (!sucursalValida && sucursalRef.current) {
            sucursalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const getPagoDetalleLinea = (pago: TipoPagoData): string => {
        if (pago.detalle) return pago.detalle;
        if (pago.tipoPago === 'Cheque') {
            const parts = [pago.banco, pago.nroCheque ? `Nº ${pago.nroCheque}` : ''].filter(Boolean);
            return parts.join(' · ');
        }
        if (pago.tipoPago === 'Transferencia' || pago.tipoPago === 'Efectivo' || pago.tipoPago === 'Otros') {
            return pago.mcp || '';
        }
        if (pago.tipoPago === 'Tarjeta') {
            return pago.nroTarjeta ? `Tarjeta ...${String(pago.nroTarjeta).slice(-4)}` : '';
        }
        return '';
    };

    const renderCamposPago = () => {
        switch (tipoPagoSeleccionado) {
            case 'Efectivo':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                EFECTIVO - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                    {mediosEfectivo.map((medio) => (
                                        <Grid item xs={12} key={medio.id}>
                                            <Card
                                                variant={efectivoSeleccionadoId === medio.id ? 'elevation' : 'outlined'}
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    setEfectivoSeleccionadoId(medio.id);
                                                    setPagoActual(prev => ({ ...prev, medioLabel: medio.nombre, mcp: medio.mcp || medio.nombre }));
                                                }}
                                            >
                                                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <input
                                                        type="radio"
                                                        checked={efectivoSeleccionadoId === medio.id}
                                                        onChange={() => {
                                                            setEfectivoSeleccionadoId(medio.id);
                                                            setPagoActual(prev => ({ ...prev, medioLabel: medio.nombre, mcp: medio.mcp || medio.nombre }));
                                                        }}
                                                        style={{ marginRight: 8 }}
                                                    />
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                        {medio.descripcion && (
                                                            <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                        )}
                                                        {medio.clase && (
                                                            <Typography variant="caption">{medio.clase}</Typography>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                    {mediosEfectivo.length === 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2">No hay medios de efectivo disponibles para este grupo.</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Monto"
                                name="importe"
                                type="number"
                                value={
                                    isMontoFocused
                                        ? (pagoActual.importe === 0 ? '' : pagoActual.importe)
                                        : (Number.isFinite(pagoActual.importe as any) ? Number(pagoActual.importe).toFixed(2) : '')
                                }
                                onChange={handlePagoFieldChange}
                                onFocus={() => setIsMontoFocused(true)}
                                onBlur={() => {
                                    setIsMontoFocused(false);
                                    setPagoActual(prev => ({
                                        ...prev,
                                        importe: truncToDecimals(prev.importe as number | string)
                                    }));
                                }}
                                inputProps={{ step: '0.01', inputMode: 'decimal' }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );

            case 'Transferencia':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                TRANSFERENCIAS - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <RadioGroup
                                    name="transferOption"
                                    value={cuentaTransferSeleccionadaId != null ? String(cuentaTransferSeleccionadaId) : ''}
                                    onChange={(e) => {
                                        const newId = String(e.target.value);
                                        setCuentaTransferSeleccionadaId(newId);
                                        const idx = Number(newId);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosTransferencia.length) {
                                            const medioSel = mediosTransferencia[idx];
                                            const idNum = Number(medioSel.id);
                                            setPagoActual(prev => ({ ...prev, nroCuenta: Number.isFinite(idNum) ? idNum : undefined, medioLabel: medioSel.nombre, mcp: medioSel.mcp || medioSel.nombre }));
                                        }
                                    }}
                                >
                                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                        {mediosTransferencia.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(cuentaTransferSeleccionadaId) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosTransferencia.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios de transferencia disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Monto"
                                name="importe"
                                type="number"
                                value={isMontoFocused && pagoActual.importe === 0 ? '' : pagoActual.importe}
                                onChange={handlePagoFieldChange}
                                onFocus={() => setIsMontoFocused(true)}
                                onBlur={() => setIsMontoFocused(false)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );

            case 'Tarjeta': {
                const usaSeleccionCuponesCartera =
                    tipoMovimientoInicial === COD_EGRESO ||
                    (origenMovimiento === 'bancario' && tipoMovimientoInicial === COD_INGRESO);

                if (usaSeleccionCuponesCartera) {
                    return (
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    TARJETAS - Seleccione el medio específico:
                                </Typography>
                                <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                    <RadioGroup
                                        name="tarjetaOption"
                                        value={tarjetaSeleccionadaIdx ?? ''}
                                        onChange={(e) => {
                                            const newIdx = String(e.target.value);
                                            setTarjetaSeleccionadaIdx(newIdx);
                                            const idx = Number(newIdx);
                                            if (!isNaN(idx) && idx >= 0 && idx < mediosTarjeta.length) {
                                                const medioSel = mediosTarjeta[idx];
                                                setPagoActual(prev => ({
                                                    ...prev,
                                                    medioLabel: medioSel.nombre,
                                                    mcp: medioSel.mcp || medioSel.nombre,
                                                }));
                                            }
                                        }}
                                    >
                                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                            {mediosTarjeta.map((medio, idx) => (
                                                <Grid item xs={12} key={medio.id ?? idx}>
                                                    <Card
                                                        variant={(String(tarjetaSeleccionadaIdx) === String(idx)) ? 'elevation' : 'outlined'}
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setTarjetaSeleccionadaIdx(String(idx));
                                                            const m = mediosTarjeta[idx];
                                                            setPagoActual(prev => ({ ...prev, medioLabel: m.nombre, mcp: m.mcp || m.nombre }));
                                                        }}
                                                    >
                                                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <FormControlLabel
                                                                value={String(idx)}
                                                                control={<Radio />}
                                                                label={
                                                                    <Box>
                                                                        <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                        {medio.descripcion && <Typography variant="caption" display="block">{medio.descripcion}</Typography>}
                                                                        {medio.clase && <Typography variant="caption">{medio.clase}</Typography>}
                                                                    </Box>
                                                                }
                                                                sx={{ m: 0 }}
                                                            />
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                            {mediosTarjeta.length === 0 && (
                                                <Grid item xs={12}>
                                                    <Typography variant="body2">No hay medios de tarjeta disponibles para este grupo.</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </RadioGroup>
                                </Box>
                            </Grid>
                            {esCuponCartera && (
                                <>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                Seleccione uno o varios cupones de cartera:
                                            </Typography>
                                            {isTabletOrLarger && cuponesCarteraDisponibles.length > 0 && (() => {
                                                const terminoBusqueda = busquedaCupon.toLowerCase().trim();
                                                const montoBusqueda = parseFloat(terminoBusqueda.replace(/[^\d.,]/g, '').replace(',', '.'));
                                                const filtrados = terminoBusqueda ? cuponesCarteraDisponibles.filter(cupon => {
                                                    const coincideTexto =
                                                        cupon.nombreMarca?.toLowerCase().includes(terminoBusqueda) ||
                                                        String(cupon.nroComprobanteTa).includes(terminoBusqueda) ||
                                                        cupon.numeroroAutorizacion?.toLowerCase().includes(terminoBusqueda) ||
                                                        cupon.titularTarjeta?.toLowerCase().includes(terminoBusqueda) ||
                                                        cupon.nroTarjeta?.toLowerCase().includes(terminoBusqueda);
                                                    const coincideMonto = !isNaN(montoBusqueda) && Math.abs((cupon.montoOperacion ?? 0) - montoBusqueda) < 0.01;
                                                    return coincideTexto || coincideMonto;
                                                }) : cuponesCarteraDisponibles;
                                                const ordenados = ordenarCupones(filtrados);
                                                const todos = ordenados.length > 0 && ordenados.every(cupon => cuponesCarteraSeleccionados.has(cupon.nroCarga));
                                                return (
                                                    <Button variant="outlined" size="small" onClick={() => handleSeleccionarTodosCupones(ordenados)} sx={{ minWidth: '140px' }}>
                                                        {todos ? 'Deseleccionar todos' : 'Seleccionar todos'}
                                                    </Button>
                                                );
                                            })()}
                                        </Box>
                                        <TextField
                                            fullWidth
                                            placeholder="Buscar (marca, cupón, autorización, titular, monto...)"
                                            value={busquedaCupon}
                                            onChange={(e) => setBusquedaCupon(e.target.value)}
                                            size="small"
                                            sx={{ mb: 2 }}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                                        />
                                        {loadingCuponesCartera ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><Typography>Cargando cupones de cartera...</Typography></Box>
                                        ) : cuponesCarteraDisponibles.length === 0 ? (
                                            <Box sx={{ p: 2, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">{cuponesCartera.length === 0 ? 'No hay cupones disponibles en cartera.' : 'Todos los cupones en cartera ya fueron agregados al movimiento.'}</Typography></Box>
                                        ) : (() => {
                                            const terminoBusqueda = busquedaCupon.toLowerCase().trim();
                                            const cuponesFiltrados = terminoBusqueda ? cuponesCarteraDisponibles.filter(cupon => {
                                                const coincideTexto =
                                                    cupon.nombreMarca?.toLowerCase().includes(terminoBusqueda) ||
                                                    String(cupon.nroComprobanteTa).includes(terminoBusqueda) ||
                                                    cupon.numeroroAutorizacion?.toLowerCase().includes(terminoBusqueda) ||
                                                    cupon.titularTarjeta?.toLowerCase().includes(terminoBusqueda) ||
                                                    cupon.nroTarjeta?.toLowerCase().includes(terminoBusqueda);
                                                const montoBusqueda = parseFloat(terminoBusqueda.replace(/[^\d.,]/g, '').replace(',', '.'));
                                                const coincideMonto = !isNaN(montoBusqueda) && Math.abs((cupon.montoOperacion ?? 0) - montoBusqueda) < 0.01;
                                                return coincideTexto || coincideMonto;
                                            }) : cuponesCarteraDisponibles;
                                            const cuponesOrdenados = ordenarCupones(cuponesFiltrados);
                                            const todosSeleccionados = cuponesOrdenados.length > 0 && cuponesOrdenados.every(cupon => cuponesCarteraSeleccionados.has(cupon.nroCarga));
                                            if (isTabletOrLarger) {
                                                return (
                                                    <TableContainer component={Paper} sx={{ maxHeight: { xs: '50vh', sm: 400 }, overflowY: 'auto' }}>
                                                        <Table size="small" stickyHeader>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell padding="checkbox" sx={{ width: 50 }}>
                                                                        <Checkbox indeterminate={cuponesCarteraSeleccionados.size > 0 && !todosSeleccionados} checked={todosSeleccionados && cuponesOrdenados.length > 0} onChange={() => handleSeleccionarTodosCupones(cuponesOrdenados)} inputProps={{ 'aria-label': 'seleccionar todos' }} />
                                                                    </TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderByCupon === 'nombreMarca'} direction={orderByCupon === 'nombreMarca' ? orderCupon : 'asc'} onClick={() => handleRequestSortCupon('nombreMarca')}>Marca</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderByCupon === 'nroComprobanteTa'} direction={orderByCupon === 'nroComprobanteTa' ? orderCupon : 'asc'} onClick={() => handleRequestSortCupon('nroComprobanteTa')}>N° Cupón</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderByCupon === 'numeroroAutorizacion'} direction={orderByCupon === 'numeroroAutorizacion' ? orderCupon : 'asc'} onClick={() => handleRequestSortCupon('numeroroAutorizacion')}>Autorización</TableSortLabel></TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 600 }}><TableSortLabel active={orderByCupon === 'montoOperacion'} direction={orderByCupon === 'montoOperacion' ? orderCupon : 'asc'} onClick={() => handleRequestSortCupon('montoOperacion')}>Monto</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderByCupon === 'titularTarjeta'} direction={orderByCupon === 'titularTarjeta' ? orderCupon : 'asc'} onClick={() => handleRequestSortCupon('titularTarjeta')}>Titular</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderByCupon === 'fecha'} direction={orderByCupon === 'fecha' ? orderCupon : 'asc'} onClick={() => handleRequestSortCupon('fecha')}>Fecha</TableSortLabel></TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {cuponesOrdenados.length === 0 ? (
                                                                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><Typography variant="body2" color="text.secondary">No se encontraron cupones que coincidan con la búsqueda</Typography></TableCell></TableRow>
                                                                ) : cuponesOrdenados.map((cupon) => {
                                                                    const estaSeleccionado = cuponesCarteraSeleccionados.has(cupon.nroCarga);
                                                                    return (
                                                                        <TableRow key={cupon.nroCarga} hover selected={estaSeleccionado} sx={{ cursor: 'pointer', bgcolor: estaSeleccionado ? 'action.selected' : 'inherit' }} onClick={() => { const nuevoSet = new Set(cuponesCarteraSeleccionados); if (estaSeleccionado) nuevoSet.delete(cupon.nroCarga); else nuevoSet.add(cupon.nroCarga); setCuponesCarteraSeleccionados(nuevoSet); }}>
                                                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                                                <Checkbox checked={estaSeleccionado} onChange={(e) => { const nuevoSet = new Set(cuponesCarteraSeleccionados); if (e.target.checked) nuevoSet.add(cupon.nroCarga); else nuevoSet.delete(cupon.nroCarga); setCuponesCarteraSeleccionados(nuevoSet); }} />
                                                                            </TableCell>
                                                                            <TableCell>{cupon.nombreMarca || cupon.codigoMarca}</TableCell>
                                                                            <TableCell>{cupon.nroComprobanteTa}</TableCell>
                                                                            <TableCell>{cupon.numeroroAutorizacion}</TableCell>
                                                                            <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>{formatCurrency(cupon.montoOperacion ?? 0)}</TableCell>
                                                                            <TableCell>{cupon.titularTarjeta || '-'}</TableCell>
                                                                            <TableCell>{cupon.fecha ? new Date(cupon.fecha).toLocaleDateString() : '-'}</TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                );
                                            }
                                            return (
                                                <Box sx={{ maxHeight: { xs: '50vh', sm: 400 }, overflowY: 'auto', pr: 1 }}>
                                                    <Grid container spacing={1.5}>
                                                        {cuponesOrdenados.length === 0 ? (
                                                            <Grid item xs={12}><Typography variant="body2" color="text.secondary">No se encontraron cupones que coincidan con la búsqueda</Typography></Grid>
                                                        ) : cuponesOrdenados.map((cupon) => {
                                                            const estaSeleccionado = cuponesCarteraSeleccionados.has(cupon.nroCarga);
                                                            return (
                                                                <Grid item xs={12} key={cupon.nroCarga}>
                                                                    <Card variant={estaSeleccionado ? 'elevation' : 'outlined'} sx={{ borderColor: estaSeleccionado ? 'primary.main' : undefined }}>
                                                                        <CardContent>
                                                                            <FormControlLabel
                                                                                control={<Checkbox checked={estaSeleccionado} onChange={(e) => { const nuevoSet = new Set(cuponesCarteraSeleccionados); if (e.target.checked) nuevoSet.add(cupon.nroCarga); else nuevoSet.delete(cupon.nroCarga); setCuponesCarteraSeleccionados(nuevoSet); }} />}
                                                                                label={<Box><Typography sx={{ fontWeight: 600 }}>{cupon.nombreMarca || cupon.codigoMarca}</Typography><Typography variant="body2">N° {cupon.nroComprobanteTa} · {formatCurrency(cupon.montoOperacion ?? 0)} · {cupon.titularTarjeta || '-'}</Typography></Box>}
                                                                                sx={{ m: 0, width: '100%' }}
                                                                            />
                                                                        </CardContent>
                                                                    </Card>
                                                                </Grid>
                                                            );
                                                        })}
                                                    </Grid>
                                                </Box>
                                            );
                                        })()}
                                        {cuponesCarteraSeleccionados.size > 0 && (
                                            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{cuponesCarteraSeleccionados.size} cupón{cuponesCarteraSeleccionados.size > 1 ? 'es' : ''} seleccionado{cuponesCarteraSeleccionados.size > 1 ? 's' : ''}</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>Total: {formatCurrency(totalImporteCuponesSeleccionados)}</Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    );
                }

                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                TARJETAS - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <RadioGroup
                                    name="tarjetaOption"
                                    value={tarjetaSeleccionadaIdx ?? ''}
                                    onChange={(e) => {
                                        const newIdx = String(e.target.value);
                                        setTarjetaSeleccionadaIdx(newIdx);
                                        const idx = Number(newIdx);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosTarjeta.length) {
                                            const medioSel = mediosTarjeta[idx];
                                            const idNum = Number(medioSel.id);
                                            setPagoActual(prev => ({
                                                ...prev,
                                                nroCuenta: Number.isFinite(idNum) ? idNum : undefined,
                                                medioLabel: medioSel.nombre,
                                                mcp: medioSel.mcp || medioSel.nombre,
                                            }));
                                        }
                                    }}
                                >
                                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                        {mediosTarjeta.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(tarjetaSeleccionadaIdx) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosTarjeta.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios de tarjeta disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <SearchableSelect
                                label="* Marca"
                                required
                                options={marcasTarjetas.map((m) => ({
                                    value: m.codigoMarca,
                                    label: m.nombreMarca || m.nombre,
                                }))}
                                value={pagoActual.codigoMarca && pagoActual.codigoMarca > 0 ? pagoActual.codigoMarca : null}
                                onChange={(value) =>
                                    setPagoActual((prev) => ({ ...prev, codigoMarca: Number(value) || 0 }))
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="* N° de Tarjeta"
                                name="nroTarjeta"
                                value={pagoActual.nroTarjeta || ''}
                                onChange={handlePagoFieldChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="* N° Comprobante"
                                name="nroComprobanteTa"
                                type="number"
                                value={pagoActual.nroComprobanteTa ?? ''}
                                onChange={handlePagoFieldChange}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="* Fecha"
                                name="fecha"
                                type="date"
                                value={pagoActual.fecha || ''}
                                onChange={handlePagoFieldChange}
                                InputLabelProps={{ shrink: true }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Fecha Presentación"
                                name="fechaPresentar"
                                type="date"
                                value={pagoActual.fechaPresentar || ''}
                                onChange={handlePagoFieldChange}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="* N° Autorización/Lote"
                                name="numeroroAutorizacion"
                                value={pagoActual.numeroroAutorizacion || ''}
                                onChange={handlePagoFieldChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Teléfono"
                                name="telefono"
                                value={pagoActual.telefono || ''}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Titular"
                                name="titularTarjeta"
                                value={pagoActual.titularTarjeta || ''}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Monto"
                                name="importe"
                                type="number"
                                value={isMontoFocused && pagoActual.importe === 0 ? '' : pagoActual.importe}
                                onChange={handlePagoFieldChange}
                                onFocus={() => setIsMontoFocused(true)}
                                onBlur={() => setIsMontoFocused(false)}
                                InputProps={{
                                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
                );
            }

            case 'Cheque': {
                const usaSeleccionChequesCartera =
                    tipoMovimientoInicial === COD_EGRESO ||
                    (origenMovimiento === 'bancario' && tipoMovimientoInicial === COD_INGRESO);

                if (usaSeleccionChequesCartera) {
                    return (
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    CHEQUES DE TERCERO - Seleccione el medio específico:
                                </Typography>
                                <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                    <RadioGroup
                                        name="chequeOption"
                                        value={chequeSeleccionadoIdx ?? ''}
                                        onChange={(e) => {
                                            const newIdx = String(e.target.value);
                                            setChequeSeleccionadoIdx(newIdx);
                                            const idx = Number(newIdx);
                                            if (!isNaN(idx) && idx >= 0 && idx < mediosCheques.length) {
                                                const medioSel = mediosCheques[idx];
                                                setPagoActual(prev => ({ ...prev, medioLabel: medioSel.nombre, mcp: medioSel.mcp || medioSel.nombre }));
                                            }
                                        }}
                                    >
                                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                            {mediosCheques.map((medio, idx) => (
                                                <Grid item xs={12} key={medio.id ?? idx}>
                                                    <Card variant={(String(chequeSeleccionadoIdx) === String(idx)) ? 'elevation' : 'outlined'} sx={{ cursor: 'pointer' }} onClick={() => { setChequeSeleccionadoIdx(String(idx)); const m = mediosCheques[idx]; setPagoActual(prev => ({ ...prev, medioLabel: m.nombre, mcp: m.mcp || m.nombre })); }}>
                                                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <FormControlLabel
                                                                value={String(idx)}
                                                                control={<Radio />}
                                                                label={
                                                                    <Box>
                                                                        <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                        {medio.descripcion && <Typography variant="caption" display="block">{medio.descripcion}</Typography>}
                                                                        {medio.clase && <Typography variant="caption">{medio.clase}</Typography>}
                                                                    </Box>
                                                                }
                                                                sx={{ m: 0 }}
                                                            />
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                            {mediosCheques.length === 0 && (
                                                <Grid item xs={12}>
                                                    <Typography variant="body2">No hay medios de cheques disponibles para este grupo.</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </RadioGroup>
                                </Box>
                            </Grid>
                            {esChequeCartera && (
                                <>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                Seleccione uno o varios cheques de cartera:
                                            </Typography>
                                            {isTabletOrLarger && chequesCartera.length > 0 && (() => {
                                                const terminoBusqueda = busquedaCheque.toLowerCase().trim();
                                                const montoBusqueda = parseFloat(terminoBusqueda.replace(/[^\d.,]/g, '').replace(',', '.'));
                                                const filtrados = terminoBusqueda ? chequesCartera.filter(cheque => {
                                                    const coincideTexto = cheque.nombreBanco?.toLowerCase().includes(terminoBusqueda) ||
                                                        String(cheque.nroCheque).includes(terminoBusqueda) ||
                                                        cheque.librador?.toLowerCase().includes(terminoBusqueda) ||
                                                        cheque.nombreSucursal?.toLowerCase().includes(terminoBusqueda) ||
                                                        cheque.concepto?.toLowerCase().includes(terminoBusqueda) ||
                                                        String(cheque.nroSucursal).includes(terminoBusqueda);
                                                    const coincideMonto = !isNaN(montoBusqueda) && Math.abs(cheque.importe - montoBusqueda) < 0.01;
                                                    return coincideTexto || coincideMonto;
                                                }) : chequesCartera;
                                                const ordenados = ordenarCheques(filtrados);
                                                const todos = ordenados.length > 0 && ordenados.every(cheque => chequesCarteraSeleccionados.has(cheque.nroCarga));
                                                return (
                                                    <Button variant="outlined" size="small" onClick={() => handleSeleccionarTodos(ordenados)} sx={{ minWidth: '140px' }}>
                                                        {todos ? 'Deseleccionar todos' : 'Seleccionar todos'}
                                                    </Button>
                                                );
                                            })()}
                                        </Box>
                                        <TextField
                                            fullWidth
                                            placeholder="Buscar por banco, número de cheque, librador, monto..."
                                            value={busquedaCheque}
                                            onChange={(e) => setBusquedaCheque(e.target.value)}
                                            size="small"
                                            sx={{ mb: 2 }}
                                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                                        />
                                        {loadingChequesCartera ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><Typography>Cargando cheques de cartera...</Typography></Box>
                                        ) : chequesCartera.length === 0 ? (
                                            <Box sx={{ p: 2, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No hay cheques disponibles en cartera.</Typography></Box>
                                        ) : (() => {
                                            const terminoBusqueda = busquedaCheque.toLowerCase().trim();
                                            const chequesFiltrados = terminoBusqueda ? chequesCartera.filter(cheque => {
                                                const coincideTexto = cheque.nombreBanco?.toLowerCase().includes(terminoBusqueda) || String(cheque.nroCheque).includes(terminoBusqueda) || cheque.librador?.toLowerCase().includes(terminoBusqueda) || cheque.nombreSucursal?.toLowerCase().includes(terminoBusqueda) || cheque.concepto?.toLowerCase().includes(terminoBusqueda) || String(cheque.nroSucursal).includes(terminoBusqueda);
                                                const montoBusqueda = parseFloat(terminoBusqueda.replace(/[^\d.,]/g, '').replace(',', '.'));
                                                const coincideMonto = !isNaN(montoBusqueda) && Math.abs(cheque.importe - montoBusqueda) < 0.01;
                                                return coincideTexto || coincideMonto;
                                            }) : chequesCartera;
                                            const chequesOrdenados = ordenarCheques(chequesFiltrados);
                                            const todosSeleccionados = chequesOrdenados.length > 0 && chequesOrdenados.every(cheque => chequesCarteraSeleccionados.has(cheque.nroCarga));
                                            if (isTabletOrLarger) {
                                                return (
                                                    <TableContainer component={Paper} sx={{ maxHeight: { xs: '50vh', sm: 400 }, overflowY: 'auto' }}>
                                                        <Table size="small" stickyHeader>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell padding="checkbox" sx={{ width: 50 }}>
                                                                        <Checkbox indeterminate={chequesCarteraSeleccionados.size > 0 && !todosSeleccionados} checked={todosSeleccionados && chequesOrdenados.length > 0} onChange={() => handleSeleccionarTodos(chequesOrdenados)} inputProps={{ 'aria-label': 'seleccionar todos' }} />
                                                                    </TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'nombreBanco'} direction={orderBy === 'nombreBanco' ? order : 'asc'} onClick={() => handleRequestSort('nombreBanco')}>Banco</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'nroCheque'} direction={orderBy === 'nroCheque' ? order : 'asc'} onClick={() => handleRequestSort('nroCheque')}>N° Cheque</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'nombreSucursal'} direction={orderBy === 'nombreSucursal' ? order : 'asc'} onClick={() => handleRequestSort('nombreSucursal')}>Sucursal</TableSortLabel></TableCell>
                                                                    <TableCell align="right" sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'importe'} direction={orderBy === 'importe' ? order : 'asc'} onClick={() => handleRequestSort('importe')}>Monto</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'librador'} direction={orderBy === 'librador' ? order : 'asc'} onClick={() => handleRequestSort('librador')}>Librador</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'fechaEmision'} direction={orderBy === 'fechaEmision' ? order : 'asc'} onClick={() => handleRequestSort('fechaEmision')}>Emisión</TableSortLabel></TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}><TableSortLabel active={orderBy === 'fechaVencimiento'} direction={orderBy === 'fechaVencimiento' ? order : 'asc'} onClick={() => handleRequestSort('fechaVencimiento')}>Vencimiento</TableSortLabel></TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {chequesOrdenados.length === 0 ? (
                                                                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}><Typography variant="body2" color="text.secondary">No se encontraron cheques que coincidan con la búsqueda</Typography></TableCell></TableRow>
                                                                ) : chequesOrdenados.map((cheque) => {
                                                                    const estaSeleccionado = chequesCarteraSeleccionados.has(cheque.nroCarga);
                                                                    return (
                                                                        <TableRow key={cheque.nroCarga} hover selected={estaSeleccionado} sx={{ cursor: 'pointer', bgcolor: estaSeleccionado ? 'action.selected' : 'inherit' }} onClick={() => { const nuevoSet = new Set(chequesCarteraSeleccionados); if (estaSeleccionado) nuevoSet.delete(cheque.nroCarga); else nuevoSet.add(cheque.nroCarga); setChequesCarteraSeleccionados(nuevoSet); }}>
                                                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                                                <Checkbox checked={estaSeleccionado} onChange={(e) => { const nuevoSet = new Set(chequesCarteraSeleccionados); if (e.target.checked) nuevoSet.add(cheque.nroCarga); else nuevoSet.delete(cheque.nroCarga); setChequesCarteraSeleccionados(nuevoSet); }} />
                                                                            </TableCell>
                                                                            <TableCell>{cheque.nombreBanco}</TableCell>
                                                                            <TableCell>{cheque.nroCheque}</TableCell>
                                                                            <TableCell>{cheque.nombreSucursal} ({cheque.nroSucursal})</TableCell>
                                                                            <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>{formatCurrency(cheque.importe)}</TableCell>
                                                                            <TableCell>{cheque.librador}</TableCell>
                                                                            <TableCell>{cheque.fechaEmision ? new Date(cheque.fechaEmision).toLocaleDateString() : '-'}</TableCell>
                                                                            <TableCell>{cheque.fechaVencimiento ? new Date(cheque.fechaVencimiento).toLocaleDateString() : '-'}</TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                );
                                            }
                                            return (
                                                <Box sx={{ maxHeight: { xs: '50vh', sm: 400 }, overflowY: 'auto', pr: 1 }}>
                                                    <Grid container spacing={1.5}>
                                                        {chequesOrdenados.length === 0 ? (
                                                            <Grid item xs={12}><Typography variant="body2" color="text.secondary">No se encontraron cheques que coincidan con la búsqueda</Typography></Grid>
                                                        ) : chequesOrdenados.map((cheque) => {
                                                            const estaSeleccionado = chequesCarteraSeleccionados.has(cheque.nroCarga);
                                                            return (
                                                                <Grid item xs={12} key={cheque.nroCarga}>
                                                                    <Card variant={estaSeleccionado ? 'elevation' : 'outlined'} sx={{ borderColor: estaSeleccionado ? 'primary.main' : undefined }}>
                                                                        <CardContent>
                                                                            <FormControlLabel
                                                                                control={<Checkbox checked={estaSeleccionado} onChange={(e) => { const nuevoSet = new Set(chequesCarteraSeleccionados); if (e.target.checked) nuevoSet.add(cheque.nroCarga); else nuevoSet.delete(cheque.nroCarga); setChequesCarteraSeleccionados(nuevoSet); }} />}
                                                                                label={<Box><Typography sx={{ fontWeight: 600 }}>{cheque.nombreBanco}</Typography><Typography variant="body2">N° {cheque.nroCheque} · {formatCurrency(cheque.importe)} · {cheque.librador}</Typography></Box>}
                                                                                sx={{ m: 0, width: '100%' }}
                                                                            />
                                                                        </CardContent>
                                                                    </Card>
                                                                </Grid>
                                                            );
                                                        })}
                                                    </Grid>
                                                </Box>
                                            );
                                        })()}
                                        {chequesCarteraSeleccionados.size > 0 && (
                                            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{chequesCarteraSeleccionados.size} cheque{chequesCarteraSeleccionados.size > 1 ? 's' : ''} seleccionado{chequesCarteraSeleccionados.size > 1 ? 's' : ''}</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>Total: {formatCurrency(totalImporteSeleccionado)}</Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    );
                }
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <SearchableSelect
                                label="Medio de Cobro"
                                required
                                options={mediosCheques.map((medio, idx) => ({
                                    value: String(idx),
                                    label: medio.descripcion || medio.nombre || medio.mcp || `Medio ${idx + 1}`,
                                }))}
                                value={chequeSeleccionadoIdx || null}
                                onChange={handleChequeMedioChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Banco" value={pagoActual.banco || ''} placeholder="Seleccionar banco..." required InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><IconButton onClick={() => { setOpenBancoDialogPara('cheque'); setOpenBancoDialog(true); }} size="small"><ArrowDropDownIcon /></IconButton></InputAdornment> }} onClick={() => { setOpenBancoDialogPara('cheque'); setOpenBancoDialog(true); }} />
                        </Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="N° Sucursal" name="nroSucursal" type="number" value={pagoActual.nroSucursal || ''} onChange={handlePagoFieldChange} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="N° Cheque" name="nroCheque" value={pagoActual.nroCheque || ''} onChange={handlePagoFieldChange} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Librador" name="librador" value={pagoActual.librador || ''} onChange={handlePagoFieldChange} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="CUIT del librador" name="nroLibrador" value={pagoActual.nroLibrador || ''} onChange={handlePagoFieldChange} placeholder="Ej: 20-12345678-9" inputProps={{ maxLength: 13 }} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Fecha Emisión" name="fechaEmision" type="date" value={pagoActual.fechaEmision || ''} onChange={handlePagoFieldChange} InputLabelProps={{ shrink: true }} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Fecha Vencimiento" name="fechaVencimiento" type="date" value={pagoActual.fechaVencimiento || ''} onChange={handlePagoFieldChange} InputLabelProps={{ shrink: true }} required /></Grid>
                        <Grid item xs={12} sm={6}><TextField fullWidth label="Importe" name="importe" type="number" value={pagoActual.importe || ''} onChange={handlePagoFieldChange} InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }} required /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Observaciones" name="detalle" value={pagoActual.detalle || ''} onChange={handlePagoFieldChange} multiline rows={2} /></Grid>
                    </Grid>
                );
            }
            case 'Otros':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                OTROS - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <RadioGroup
                                    name="otrosOption"
                                    value={otrosSeleccionadoIdx ?? ''}
                                    onChange={(e) => {
                                        const newIdx = String(e.target.value);
                                        setOtrosSeleccionadoIdx(newIdx);
                                        const idx = Number(newIdx);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosOtros.length) {
                                            const medioSel = mediosOtros[idx];
                                            const idNum = Number(medioSel.id);
                                            setPagoActual(prev => ({ ...prev, nroCuenta: Number.isFinite(idNum) ? idNum : undefined, medioLabel: medioSel.nombre, mcp: medioSel.mcp || medioSel.nombre }));
                                        }
                                    }}
                                >
                                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                        {mediosOtros.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(otrosSeleccionadoIdx) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosOtros.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Monto"
                                name="importe"
                                type="number"
                                value={isMontoFocused && pagoActual.importe === 0 ? '' : pagoActual.importe}
                                onChange={handlePagoFieldChange}
                                onFocus={() => setIsMontoFocused(true)}
                                onBlur={() => setIsMontoFocused(false)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );

            default:
                return null;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setShowValidation(true);

        if (!tipoMovimientoConfirmado || !tipoMovimientoInicial) {
            enqueueSnackbar('Debe seleccionar el tipo de movimiento', { variant: 'warning' });
            return;
        }
        
        if (!esMovimientoBancario && !imputableSeleccionado) {
            enqueueSnackbar('Debe seleccionar un concepto', { variant: 'warning' });
            scrollToFirstError();
            return;
        }

        if (!formulario.nroSuc || formulario.nroSuc <= 0) {
            enqueueSnackbar('Debe seleccionar un N° Sucursal válido.', { variant: 'warning' });
            scrollToFirstError();
            return;
        }

        // Validación para depósito (55) o extracción (56) bancaria: debe haber banco de cartera seleccionado.
        if (origenMovimiento === 'bancario' && (tipoActual === COD_INGRESO || tipoActual === COD_EGRESO)) {
            const codigoEntidadNum = Number(formulario.codigoEntidad);
            if (!Number.isFinite(codigoEntidadNum) || codigoEntidadNum <= 0) {
                enqueueSnackbar('Debe seleccionar un banco de cartera (NroBanco ≥ 1000).', { variant: 'warning' });
                return;
            }
        }

        if (detallesPago.length === 0 || totalMedios <= 0) {
            enqueueSnackbar('Debe agregar al menos un medio de pago con monto > 0', { variant: 'warning' });
            return;
        }

        const grupoMCP = grupoCajaSeleccionado?.codigo;
        if (!grupoMCP || !Number.isFinite(grupoMCP)) {
            enqueueSnackbar('Debe seleccionar un Grupo de Caja válido', { variant: 'warning' });
            setOpenGrupoCajaDialog(true);
            return;
        }

        // Determinar tipoMovimiento según selección inicial (el backend espera STRING).
        const tipoMovimiento = tipoActual;

        // Construir mediosDeCobro (en camelCase como otros endpoints de Tesorería)
        const mediosDeCobro = detallesPago.map((p) => {
            const base: any = {
                mcp: p.mcp || '',
                importe: Number(p.importe) || 0, // Asegurar que sea número, no string
                detalle: p.detalle || '',
            };

            if (p.tipoPago === 'Cheque') {
                base.cheque = {
                    nroBanco: p.nroBanco ?? 0,
                    nroSucursal: p.nroSucursal ?? 0,
                    nroCheque: p.nroCheque || '',
                    nroLibrador: p.nroLibrador || '',
                    librador: p.librador || '',
                    fechaEmision: p.fechaEmision ? new Date(p.fechaEmision).toISOString() : new Date().toISOString(),
                    fechaVencimiento: p.fechaVencimiento ? new Date(p.fechaVencimiento).toISOString() : new Date().toISOString(),
                    ctaBancaria: p.ctaBancaria || '',
                    concepto: p.concepto || '',
                    ...(p.nroCarga ? { nroCarga: p.nroCarga } : {}),
                };
            }

            if (p.tipoPago === 'Tarjeta') {
                // Cupón de cartera: solo nroCarga (igual que traspasos). El backend marca la salida.
                // Alta manual (mov. varios): objeto tarjeta completo.
                base.tarjeta = p.nroCarga
                    ? { nroCarga: p.nroCarga }
                    : {
                        codigoMarca: Number(p.codigoMarca) || 0,
                        nroTarjeta: p.nroTarjeta || '',
                        nroComprobanteTa: Number(p.nroComprobanteTa) || 0,
                        fecha: p.fecha ? new Date(p.fecha).toISOString() : new Date().toISOString(),
                        fechaPresentar: p.fechaPresentar ? new Date(p.fechaPresentar).toISOString() : new Date().toISOString(),
                        numeroroAutorizacion: p.numeroroAutorizacion || '',
                        telefono: p.telefono || '',
                        titularTarjeta: p.titularTarjeta || '',
                    };
            }

            return base;
        });

        // Validar que nroSuc esté correctamente establecido
        const nroSucEnviar = formulario.nroSuc;
        console.log('🔍 Validación antes de enviar:');
        console.log('  - formulario.nroSuc:', formulario.nroSuc);
        console.log('  - tipo:', typeof formulario.nroSuc);
        console.log('  - es válido (> 0):', nroSucEnviar > 0);

        if (!nroSucEnviar || nroSucEnviar <= 0) {
            enqueueSnackbar('Debe seleccionar un N° Sucursal válido antes de guardar', { variant: 'error' });
            return;
        }

        const payload = {
            fecha: formulario.fechaEmision ? new Date(formulario.fechaEmision).toISOString() : new Date().toISOString(),
            grupoMCP: grupoMCP,
            nroSuc: nroSucEnviar,
            tipoMovimiento: tipoMovimiento,
            codigoEntidad: formulario.codigoEntidad,
            descripcion: esMovimientoBancario ? '' : formulario.descripcion,
            aclaracion: formulario.aclaracion,
            mediosDeCobro: mediosDeCobro,
        };

        console.log('📤 Enviando movimiento de caja (directo):', payload);
        console.log('📤 Payload completo:', JSON.stringify(payload, null, 2));
        console.log('📤 nroSuc en payload:', payload.nroSuc, '(tipo:', typeof payload.nroSuc, ')');
        console.log('📤 grupoMCP en payload:', payload.grupoMCP, '(tipo:', typeof payload.grupoMCP, ')');
        console.log('📤 tipoMovimiento en payload:', payload.tipoMovimiento, '(tipo:', typeof payload.tipoMovimiento, ')');
        console.log('📤 mediosDeCobro en payload:', payload.mediosDeCobro?.length || 0, 'elementos');
        console.log('📤 Primer medio de cobro:', payload.mediosDeCobro?.[0]);
        console.log('📤 Importe del primer medio:', payload.mediosDeCobro?.[0]?.importe, '(tipo:', typeof payload.mediosDeCobro?.[0]?.importe, ')');

        try {
            setSaving(true);
            await api.post('/api/TesoreriaCaja/movimientos-caja', payload);
            enqueueSnackbar('Movimiento creado exitosamente', { variant: 'success' });
            navigate(listadoPath);
        } catch (error: any) {
            console.error('❌ Error al guardar movimiento:', error);

            let mensaje = 'Error al guardar el movimiento';
            const data = error?.response?.data;

            if (data) {
                if (typeof data === 'string') {
                    mensaje = data;
                } else if (data.message) {
                    mensaje = data.message;
                } else if (data.title) {
                    mensaje = data.title;
                }

                // Extraer el primer mensaje de validación si existe data.errors (típico de ASP.NET)
                if (data.errors && typeof data.errors === 'object') {
                    const firstKey = Object.keys(data.errors)[0];
                    const firstError = (data.errors as any)[firstKey]?.[0];
                    if (firstError) {
                        mensaje = `${firstKey}: ${firstError}`;
                    }
                }

                try {
                    console.log('🔍 Detalle de error de validación movimientos-caja (JSON):', JSON.stringify(data, null, 2));
                } catch {
                    console.error('🔍 Detalle de error de validación movimientos-caja:', data);
                }
            }

            enqueueSnackbar(mensaje, { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1, sm: 2 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} mb={2.5} flexWrap="wrap">
                <Button startIcon={<ArrowBackIcon />} onClick={handleVolver} size="small" sx={{ textTransform: 'none' }}>
                    {listadoLabel}
                </Button>
                <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                <Typography
                    variant={isSmallScreen ? 'h5' : 'h4'}
                    sx={{ fontWeight: 600, color: '#1976d2', fontSize: { xs: '1.5rem', sm: '2rem' } }}
                >
                    {pageTitle}
                </Typography>
            </Stack>

            <Card
                sx={{
                    bgcolor: '#ffffff',
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <form onSubmit={handleSubmit}>
                        {!tipoMovimientoConfirmado ? (
                            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent>
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        display="block"
                                        mb={1.5}
                                        sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                                    >
                                        Tipo de movimiento
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" mb={2}>
                                        Elegí el tipo de movimiento para continuar con la carga.
                                    </Typography>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <Button
                                            variant="outlined"
                                            color="success"
                                            fullWidth
                                            size="large"
                                            startIcon={<ArrowUpwardIcon />}
                                            onClick={() => handleSelectTipo(COD_INGRESO)}
                                            sx={{
                                                textTransform: 'none',
                                                py: 2,
                                                borderWidth: 2,
                                                fontWeight: 600,
                                                '&:hover': { borderWidth: 2 },
                                            }}
                                        >
                                            {LABEL_INGRESO}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            fullWidth
                                            size="large"
                                            startIcon={<ArrowDownwardIcon />}
                                            onClick={() => handleSelectTipo(COD_EGRESO)}
                                            sx={{
                                                textTransform: 'none',
                                                py: 2,
                                                borderWidth: 2,
                                                fontWeight: 600,
                                                '&:hover': { borderWidth: 2 },
                                            }}
                                        >
                                            {LABEL_EGRESO}
                                        </Button>
                                    </Stack>
                                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
                                        <Button variant="outlined" onClick={handleVolver} sx={{ textTransform: 'none' }}>
                                            Cancelar
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        ) : (
                            <>
                                <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
                                    <Grid item xs={12} md={8}>
                                        {!esMovimientoBancario && (
                                            <Box ref={motivoRef}>
                                                <Typography
                                                    variant="overline"
                                                    color="text.secondary"
                                                    display="block"
                                                    mb={0.75}
                                                    sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                                                >
                                                    Concepto
                                                </Typography>
                                                {!imputableSeleccionado ? (
                                                    <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                                                        <Box sx={{ flex: 1, minWidth: 200 }}>
                                                            <ImputableInlineSearchField
                                                                label="Concepto imputable"
                                                                placeholder="Número de cuenta o nombre"
                                                                helperText={
                                                                    showValidation && !motivoValido
                                                                        ? 'Debe seleccionar un concepto imputable'
                                                                        : 'Escriba el número o nombre para buscar'
                                                                }
                                                                tipoMovimientoIE={esEgreso ? 'E' : 'I'}
                                                                onSelect={handleImputableSelect}
                                                                onOpenModal={() => setOpenImputableSearch(true)}
                                                                error={showValidation && !motivoValido}
                                                            />
                                                        </Box>
                                                        <Button
                                                            type="button"
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() => setOpenImputableSearch(true)}
                                                            sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0, mt: 0.25 }}
                                                        >
                                                            Seleccionar concepto
                                                        </Button>
                                                    </Stack>
                                                ) : (
                                                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                                                <Box flex={1} minWidth={0}>
                                                                    <Typography variant="subtitle1" fontWeight={600}>
                                                                        {imputableSeleccionado.nomCuenta || 'Sin nombre'}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        Cuenta #{imputableSeleccionado.nroCuenta}
                                                                    </Typography>
                                                                </Box>
                                                                <Stack direction="row" spacing={0} flexShrink={0}>
                                                                    <Tooltip title="Cambiar concepto">
                                                                        <IconButton size="small" onClick={() => setOpenImputableSearch(true)} aria-label="Cambiar concepto">
                                                                            <Edit fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Quitar concepto">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => {
                                                                                setImputableSeleccionado(null);
                                                                                setFormulario((prev) => ({ ...prev, codigoEntidad: 0, descripcion: '' }));
                                                                            }}
                                                                            aria-label="Quitar concepto"
                                                                        >
                                                                            <DeleteOutline fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                )}
                                            </Box>
                                        )}
                                        {esMovimientoBancario && (
                                            <Box sx={{ mt: esMovimientoBancario ? 0 : 3 }}>
                                                <Typography
                                                    variant="overline"
                                                    color="text.secondary"
                                                    display="block"
                                                    mb={0.75}
                                                    sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                                                >
                                                    Banco de cartera
                                                </Typography>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    label={esEgreso ? 'Banco (Extracción Bancaria)' : 'Banco (Depósito Bancario)'}
                                                    value={
                                                        bancoDepositoSeleccionado
                                                            ? `${bancoDepositoSeleccionado.nombre}${bancoDepositoSeleccionado.nroBanco ? ` (${bancoDepositoSeleccionado.nroBanco})` : ''}`
                                                            : formulario.codigoEntidad
                                                                ? `Código entidad: ${formulario.codigoEntidad}`
                                                                : ''
                                                    }
                                                    required
                                                    error={showValidation && !formulario.codigoEntidad}
                                                    helperText={
                                                        showValidation && !formulario.codigoEntidad
                                                            ? 'Seleccione un banco de cartera (NroBanco ≥ 1000)'
                                                            : 'Seleccione un banco de cartera (NroBanco ≥ 1000)'
                                                    }
                                                    InputProps={{
                                                        readOnly: true,
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => { setOpenBancoDialogPara('codigoEntidad'); setBancoSeleccionadoId(null); setOpenBancoDialog(true); }}
                                                                    edge="end"
                                                                    color="primary"
                                                                    size="small"
                                                                >
                                                                    <ArrowDropDownIcon />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onClick={() => { setOpenBancoDialogPara('codigoEntidad'); setBancoSeleccionadoId(null); setOpenBancoDialog(true); }}
                                                />
                                            </Box>
                                        )}

                                        <Card sx={{ boxShadow: 2, mt: 2 }}>
                                            <CardContent sx={{ p: 2 }}>
                                                <Typography
                                                    variant="overline"
                                                    color="text.secondary"
                                                    display="block"
                                                    mb={1.5}
                                                    sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                                                >
                                                    Medios de pago
                                                </Typography>

                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                                    <Tooltip
                                                        title={
                                                            !esMovimientoBancario && !imputableSeleccionado
                                                                ? 'Seleccioná un concepto imputable primero'
                                                                : ''
                                                        }
                                                    >
                                                        <span>
                                                            <Button
                                                                variant="contained"
                                                                startIcon={<AddIcon />}
                                                                onClick={handleOpenPagoDialog}
                                                                disabled={!esMovimientoBancario && !imputableSeleccionado}
                                                                fullWidth={isSmallScreen}
                                                                sx={{ textTransform: 'none' }}
                                                            >
                                                                Agregar pago
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </Box>

                                                <Grid container spacing={2}>
                                                    {detallesPago.map((pago, index) => (
                                                        <Grid item xs={12} key={index}>
                                                            <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 0 }}>
                                                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                                        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
                                                                            <Box sx={{ color: 'primary.main', display: 'flex' }}>
                                                                                {TIPO_PAGO_ICONS[pago.tipoPago || ''] || <PaymentIcon fontSize="small" />}
                                                                            </Box>
                                                                            <Box minWidth={0}>
                                                                                <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>
                                                                                    {pago.tipoPago}
                                                                                </Typography>
                                                                                {!!getPagoDetalleLinea(pago) && (
                                                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                                                        {getPagoDetalleLinea(pago)}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        </Stack>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                                                            <Typography sx={{ fontWeight: 800, color: esEgreso ? 'error.main' : 'success.main' }}>
                                                                                {formatCurrency(pago.importe || 0)}
                                                                            </Typography>
                                                                            <IconButton onClick={() => removePago(index)} size="small" sx={{ color: 'text.secondary' }} aria-label="Eliminar medio">
                                                                                <DeleteIcon />
                                                                            </IconButton>
                                                                        </Box>
                                                                    </Box>
                                                                </CardContent>
                                                            </Card>
                                                        </Grid>
                                                    ))}
                                                    {detallesPago.length === 0 && (
                                                        <Grid item xs={12}>
                                                            <Box
                                                                sx={{
                                                                    textAlign: 'center',
                                                                    py: 4,
                                                                    px: 2,
                                                                    borderRadius: 2,
                                                                    border: '1px dashed',
                                                                    borderColor: 'divider',
                                                                    bgcolor: 'grey.50',
                                                                }}
                                                            >
                                                                <PaymentIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                                                <Typography variant="body2" color="text.secondary" mb={1}>
                                                                    Agregá al menos un medio de pago para registrar el movimiento
                                                                </Typography>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    startIcon={<AddIcon />}
                                                                    onClick={handleOpenPagoDialog}
                                                                    disabled={!esMovimientoBancario && !imputableSeleccionado}
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    Agregar primer medio
                                                                </Button>
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                </Grid>

                                                <Box
                                                    sx={{
                                                        mt: 2,
                                                        p: 2,
                                                        borderRadius: 2,
                                                        borderLeft: '6px solid',
                                                        borderColor: esEgreso ? 'error.main' : 'primary.main',
                                                        bgcolor: 'background.paper',
                                                    }}
                                                >
                                                    {Array.from(totalesPorTipo.entries()).map(([tipo, monto]) => (
                                                        <Box key={tipo} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {tipo}:
                                                            </Typography>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {formatCurrency(monto)}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                                        <Typography color="text.secondary" fontWeight={600}>
                                                            Total medios:
                                                        </Typography>
                                                        <Typography sx={{ color: esEgreso ? 'error.main' : 'primary.main', fontWeight: 800 }}>
                                                            {formatCurrency(totalMedios)}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ mt: 2 }}>
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        color="warning"
                                                        fullWidth
                                                        disabled={!formularioListo || saving}
                                                        sx={{
                                                            fontSize: { xs: '0.875rem', sm: '1rem' },
                                                            py: { xs: 1.5, sm: 1 },
                                                            textTransform: 'none',
                                                        }}
                                                    >
                                                        {saving ? (
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <CircularProgress size={20} color="inherit" />
                                                                <span>Procesando...</span>
                                                            </Stack>
                                                        ) : esEgreso ? (
                                                            esMovimientoBancario ? 'Registrar extracción' : 'Registrar egreso'
                                                        ) : esMovimientoBancario ? (
                                                            'Registrar depósito'
                                                        ) : (
                                                            'Registrar ingreso'
                                                        )}
                                                    </Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
                                            <Box
                                                sx={{
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    borderRadius: 2,
                                                    p: 2,
                                                    bgcolor: 'background.paper',
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    textTransform="uppercase"
                                                    letterSpacing={0.5}
                                                    display="block"
                                                    mb={1.5}
                                                >
                                                    Datos de emisión
                                                </Typography>
                                                <Stack spacing={1.5} ref={sucursalRef}>
                                                    <SearchableSelect
                                                        label="Sucursal"
                                                        size="small"
                                                        required
                                                        loading={loadingSucursales}
                                                        options={sucursales.map((sucursal) => ({
                                                            value: sucursal.nroSuc,
                                                            label: sucursal.nombreSucursal?.trim()
                                                                ? `${sucursal.nroSuc} — ${sucursal.nombreSucursal.trim()}`
                                                                : `Suc. ${sucursal.nroSuc}`,
                                                        }))}
                                                        value={formulario.nroSuc > 0 ? formulario.nroSuc : null}
                                                        onChange={(value) => {
                                                            const nro = Number(value) || 0;
                                                            setFormulario((prev) => ({ ...prev, nroSuc: nro }));
                                                        }}
                                                    />
                                                    {showValidation && !sucursalValida && (
                                                        <Typography variant="caption" color="error">
                                                            Debe seleccionar una sucursal válida
                                                        </Typography>
                                                    )}
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        label="Fecha de emisión"
                                                        name="fechaEmision"
                                                        type="date"
                                                        value={formulario.fechaEmision}
                                                        onChange={handleFormularioChange}
                                                        InputLabelProps={{ shrink: true }}
                                                        required
                                                    />
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                                            Tipo
                                                        </Typography>
                                                        <Chip
                                                            label={esEgreso ? LABEL_EGRESO : LABEL_INGRESO}
                                                            color={esEgreso ? 'error' : 'success'}
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => {
                                                                setTipoMovimientoConfirmado(false);
                                                                setTipoMovimientoInicial(tipoActual);
                                                            }}
                                                            sx={{ cursor: 'pointer' }}
                                                        />
                                                    </Stack>
                                                    <GrupoCajaSelect
                                                        usuario={loggedUsername}
                                                        value={grupoCajaSeleccionado}
                                                        onChange={handleGrupoCajaChange}
                                                        locked={grupoCajaBloqueado}
                                                        required
                                                        error={showValidation && !grupoCajaValido}
                                                        helperText={
                                                            showValidation && !grupoCajaValido
                                                                ? 'Debe seleccionar un grupo de caja'
                                                                : undefined
                                                        }
                                                    />
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        label="Observaciones"
                                                        name="aclaracion"
                                                        value={formulario.aclaracion}
                                                        onChange={handleFormularioChange}
                                                        multiline
                                                        minRows={2}
                                                        InputProps={{
                                                            startAdornment: (
                                                                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                                                                    <NotesIcon />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                </Stack>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </form>
                </CardContent>
            </Card>

            <Dialog open={openConfirmSalir} onClose={() => setOpenConfirmSalir(false)} maxWidth="xs" fullWidth>
                <DialogTitle>¿Descartar cambios?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Hay datos cargados que se perderán si volvés al listado.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmSalir(false)}>Seguir editando</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => {
                            setOpenConfirmSalir(false);
                            navigate(listadoPath);
                        }}
                    >
                        Descartar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog para buscar imputable (concepto) — solo movimientos varios */}
                    {!esMovimientoBancario && (
                        <ImputableSearch
                            open={openImputableSearch}
                            onClose={() => setOpenImputableSearch(false)}
                            onSelect={handleImputableSelect}
                            tipoMovimientoIE={
                                (formulario.tipoMovimiento || tipoMovimientoInicial) === COD_EGRESO
                                    ? 'E'
                                    : 'I'
                            }
                        />
                    )}

            {/* Dialog para agregar/editar pagos */}
            <Dialog
                open={openPagoDialog}
                onClose={handleClosePagoDialog}
                maxWidth={esChequeCartera || esCuponCartera ? 'xl' : 'sm'}
                fullWidth
                fullScreen={isSmallScreen}
            >
                <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, pb: { xs: 1, sm: 2 } }}>
                    Agregar Pago
                </DialogTitle>
                <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                    <Box sx={{ mt: { xs: 1, sm: 2 } }}>
                        <Stepper activeStep={activeStep} sx={{ mb: { xs: 3, sm: 4 } }}>
                            <Step>
                                <StepLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Seleccionar Tipo</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Completar Datos</StepLabel>
                            </Step>
                        </Stepper>

                        {activeStep === 0 ? (
                            <SearchableSelect
                                label="Tipo de Pago"
                                options={[...TIPO_PAGO_OPTIONS]}
                                value={tipoPagoSeleccionado || null}
                                onChange={(value) => {
                                    if (value) handleTipoPagoChange(value);
                                }}
                            />
                        ) : (
                            renderCamposPago()
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
                    <Button onClick={handleClosePagoDialog}>
                        Cancelar
                    </Button>
                    {activeStep > 0 && (
                        <Button onClick={() => setActiveStep(0)}>
                            Atrás
                        </Button>
                    )}
                    {activeStep === 1 && (
                        <Button
                            onClick={handleAgregarPago}
                            variant="contained"
                            color="primary"
                            disabled={
                                (esChequeCartera && chequesCarteraSeleccionados.size === 0) ||
                                (esCuponCartera && cuponesCarteraSeleccionados.size === 0)
                            }
                        >
                            Agregar
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Diálogo de selección de Banco (igual que en carga de recibos) */}
            <Dialog
                open={openBancoDialog}
                onClose={() => { setOpenBancoDialog(false); setOpenBancoDialogPara(null); }}
                maxWidth="sm"
                fullWidth
                fullScreen={isSmallScreen}
            >
                <DialogTitle>Seleccionar Banco</DialogTitle>
                <DialogContent>
                    {openBancoDialogPara === 'codigoEntidad' && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Solo se muestran bancos de cartera (NroBanco ≥ 1000).
                        </Typography>
                    )}
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            placeholder="Buscar banco..."
                            value={bancoSearch}
                            onChange={(e) => setBancoSearch(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>
                    <Box sx={{ maxHeight: { xs: '60vh', sm: 360 }, overflowY: 'auto', pr: 1 }} onScroll={handleBancoScroll}>
                        <Grid container spacing={1}>
                            {(() => {
                                const bancosParaMostrar = openBancoDialogPara === 'codigoEntidad'
                                    ? bancos.filter((b) => {
                                        const n = Number(b.nroBanco ?? b.codigo);
                                        return Number.isFinite(n) && n >= 1000;
                                    })
                                    : bancos;
                                return (
                                    <>
                                        {bancosParaMostrar.map((banco, idx) => (
                                            <Grid item xs={12} key={banco.id ?? idx}>
                                                <Card
                                                    variant={bancoSeleccionadoId === banco.id ? 'elevation' : 'outlined'}
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => setBancoSeleccionadoId(banco.id)}
                                                >
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <input
                                                            type="radio"
                                                            checked={bancoSeleccionadoId === banco.id}
                                                            onChange={() => setBancoSeleccionadoId(banco.id)}
                                                            style={{ marginRight: 8 }}
                                                        />
                                                        <Typography sx={{ fontWeight: 600 }}>
                                                            {`${banco.nombre}${banco.nroBanco ? ` (${banco.nroBanco})` : ''}`}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {!bancoLoading && bancosParaMostrar.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">
                                                    {openBancoDialogPara === 'codigoEntidad'
                                                        ? 'No hay bancos de cartera (NroBanco ≥ 1000) en la lista. Cargue más páginas o busque otro término.'
                                                        : 'No se encontraron bancos.'}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </>
                                );
                            })()}
                            {bancoLoading && (
                                <Grid item xs={12}>
                                    <Typography variant="body2">Cargando bancos...</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenBancoDialog(false); setOpenBancoDialogPara(null); }}>Cancelar</Button>
                    <Button
                        variant="contained"
                        disabled={bancoSeleccionadoId == null}
                        onClick={() => {
                            const banco = bancos.find(b => b.id === bancoSeleccionadoId);
                            if (banco) {
                                const label = `${banco.nombre}${banco.nroBanco ? ` (${banco.nroBanco})` : ''}`;
                                const nroBancoNum = Number(banco.nroBanco);
                                const nroBancoFinal = Number.isFinite(nroBancoNum)
                                    ? nroBancoNum
                                    : (typeof banco.codigo === 'number'
                                        ? banco.codigo
                                        : Number(banco.codigo));
                                setPagoActual(prev => ({
                                    ...prev,
                                    banco: label,
                                    nroBanco: Number.isFinite(nroBancoFinal) ? nroBancoFinal : banco.id,
                                }));

                                // Depósito (55) o Extracción (56) bancario: CodigoEntidad = NroBanco del banco de cartera (>= 1000).
                                const tipoActual = (formulario.tipoMovimiento || tipoMovimientoInicial || COD_INGRESO).toString();
                                if (openBancoDialogPara === 'codigoEntidad' && origenMovimiento === 'bancario' && (tipoActual === COD_INGRESO || tipoActual === COD_EGRESO) && Number.isFinite(nroBancoFinal)) {
                                    setBancoDepositoSeleccionado(banco);
                                    setFormulario(prev => ({
                                        ...prev,
                                        codigoEntidad: nroBancoFinal,
                                    }));
                                }
                            }
                            setOpenBancoDialog(false);
                            setOpenBancoDialogPara(null);
                        }}
                    >
                        Seleccionar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openGrupoCajaDialog}
                onClose={() => {
                    setOpenGrupoCajaDialog(false);
                    setAbrirPagoTrasGrupo(false);
                }}
                maxWidth="sm"
                fullWidth
                fullScreen={isSmallScreen}
            >
                <DialogTitle>Seleccionar Grupo de Caja</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1 }}>
                        <Box sx={{ maxHeight: { xs: '60vh', sm: 360 }, overflowY: 'auto', pr: 1 }} onScroll={handleGrupoCajaScroll}>
                            <Grid container spacing={2}>
                                {gruposCaja.map((g) => (
                                    <Grid item xs={12} key={g.id}>
                                        <Card
                                            variant={grupoCajaSeleccionadoId === g.id ? 'elevation' : 'outlined'}
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => setGrupoCajaSeleccionadoId(g.id)}
                                        >
                                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <input
                                                    type="radio"
                                                    checked={grupoCajaSeleccionadoId === g.id}
                                                    onChange={() => setGrupoCajaSeleccionadoId(g.id)}
                                                    style={{ marginRight: 8 }}
                                                />
                                                <Box>
                                                    <Typography sx={{ fontWeight: 600 }}>{g.nombre}</Typography>
                                                    <Typography variant="caption">Código: {g.codigo}</Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                                {gruposCaja.length === 0 && !grupoCajaLoading && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2">No hay grupos de caja disponibles.</Typography>
                                    </Grid>
                                )}
                                {grupoCajaLoading && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2">Cargando grupos...</Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setOpenGrupoCajaDialog(false);
                            setAbrirPagoTrasGrupo(false);
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button onClick={handleConfirmarGrupoCaja} variant="contained" disabled={grupoCajaSeleccionadoId == null}>
                        Seleccionar
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default NuevoMovimientoCaja;
